import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BackHandler,
  Image,
  Linking,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { notify } from '../services/dialog';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../services/notifications';
import { payBasket, payForBookings } from '../services/payFlow';
import { basketPlacement } from '../services/checkout';
import { SafeAreaView } from 'react-native-safe-area-context';
import AuthSheet from '../components/auth/AuthSheet';
import CancelSheet from '../components/booking/CancelSheet';
import SlotSheet, { Slot } from '../components/cart/SlotSheet';
import ConfirmSheet from '../components/ui/ConfirmSheet';
import AccountScreen, { type Stat } from './AccountScreen';
import BookingDetailScreen from './BookingDetailScreen';
import BookingsScreen from './BookingsScreen';
import CartScreen from './CartScreen';
import AccountDeletedScreen from './account/AccountDeletedScreen';
import HelpSupportScreen from './account/HelpSupportScreen';
import NotificationsScreen from './account/NotificationsScreen';
import PaymentMethodsScreen from './account/PaymentMethodsScreen';
import PersonalInfoScreen from './account/PersonalInfoScreen';
import PrivacyScreen from './account/PrivacyScreen';
import RatingsScreen from './account/RatingsScreen';
import ReferralScreen from './account/ReferralScreen';
import SavedAddressesScreen from './account/SavedAddressesScreen';
import SettingsScreen from './account/SettingsScreen';
import SubscriptionScreen from './account/SubscriptionScreen';
import ChatScreen from './ChatScreen';
import DriverScreen from './DriverScreen';
import InvoiceScreen from './InvoiceScreen';
import ReviewScreen from './ReviewScreen';
import ServiceScreen from './ServiceScreen';
import ServicesScreen from './ServicesScreen';
import WalletScreen from './WalletScreen';
import LocationScreen from './LocationScreen';
import PaymentScreen from './PaymentScreen';
import SearchScreen from './SearchScreen';
import SosScreen from './SosScreen';
import SubCategoryScreen from './SubCategoryScreen';
import NewTicketScreen from './support/NewTicketScreen';
import TicketsScreen from './support/TicketsScreen';
import TicketThreadScreen from './support/TicketThreadScreen';
import ArtPanel from '../components/home/ArtPanel';
import AddressSheet from '../components/cart/AddressSheet';
import CoverageSheet from '../components/cart/CoverageSheet';
import BottomNav, { TabKey } from '../components/home/BottomNav';
import CartBar from '../components/home/CartBar';
import LiveBookingBar from '../components/home/LiveBookingBar';
import { pickBarBookings } from '../components/home/liveBarBookings';
import CatalogueSheet from '../components/home/CatalogueSheet';
import CategoryRail from '../components/home/CategoryRail';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
/* `DriverCard` - the ride banner is hidden on Home; see the commented block
   in the render. The component and the screen behind it are untouched. */
import HomeHero from '../components/home/HomeHero';
import SectionHeader from '../components/home/SectionHeader';
import ServiceCard from '../components/home/ServiceCard';
import SubCategorySheet from '../components/home/SubCategorySheet';
import Icon from '../components/ui/Icon';
import SlideIn from '../components/ui/SlideIn';
import TabFade from '../components/ui/TabFade';
import Text from '../components/ui/Text';
import { describeSlot, slotStartIso } from '../data/booking';
import { Booking, bookings as seededBookings } from '../data/bookings';
import { CancelReason } from '../data/cancellation';
import { MyReview, visiblePhotos } from '../data/reviews';
import { seededTickets, Ticket } from '../data/support';
import { launchImageLibrary } from 'react-native-image-picker';
import { AppNotification, initialNotifications } from '../data/notifications';
import { PaymentOrder } from '../data/payment';
import {
  formatPhone,
  refresh as refreshTokens,
  requestAccountDeletion,
  restoreAccount,
} from '../services/auth';
import type { Session as AuthSession } from '../services/auth';
import type * as auth from '../services/auth';
import {
  setAccessToken as setClientToken,
  setRefreshHandler,
  type RefreshOutcome,
  setUnauthorizedHandler,
} from '../services/client';
import { forgetPushToken, registerPushToken } from '../voice/push';
import { useVoiceCallOptional } from '../voice/VoiceCallProvider';
import {
  formatAddress,
  NO_ADDRESS,
  placeFromPin,
  SavedAddress,
  UNRESOLVED_TITLE,
} from '../data/places';
import {
  isNotServiceableError,
  NOT_SERVICEABLE_MESSAGE,
  reverseGeocode,
} from '../services/location';
import {
  createRecurringPlans,
  ensureServerAddress,
  resendEndOtp,
  type RescheduleAllowance,
} from '../services/bookings';
import { loadAppConfig, membershipOnSale } from '../services/config';
import {
  contentTypeFor,
  fetchPhotoUrl,
  uploadProfilePhoto,
} from '../services/profilePhoto';
import { ApiError } from '../services/types';
import { useAddresses } from '../hooks/useAddresses';
import { useBookingEvents } from '../hooks/useBookingEvents';
import { closeLiveBookings } from '../services/liveBookings';
import { useBookings } from '../hooks/useBookings';
import { useCatalogue } from '../hooks/useCatalogue';
import { useServiceCity } from '../hooks/useCoverage';
import { useUnavailableServices } from '../hooks/useAreaAvailability';
import {
  getAreaAvailability,
  OPEN_AVAILABILITY,
  setAreaAvailability,
  unavailableCategories,
} from '../services/areaAvailability';
import AreaComingSoonSheet from '../components/home/AreaComingSoonSheet';
import AreaNotServedNotice from '../components/home/AreaNotServedNotice';
import { hintsFrom, popularFrom } from '../services/catalogue';
import { fetchHomeConfig, sectionOf, type UiTree } from '../services/uiConfig';
import ConfigRenderer from '../components/home/ConfigRenderer';

/**
 * The referral banner's artwork.
 *
 * A whole scene rather than a cut-out: a pale green ground with the gift and
 * the coins on the right and a deliberately EMPTY left. So it is the card's
 * background and the copy sits on top of it, in the space the picture leaves -
 * not a picture placed beside text.
 *
 * Two things about it are load-bearing rather than decorative, and both are
 * why it was redrawn:
 *
 *  - it is 2241x702, which is `REFER_ASPECT`, so `cover` crops nothing. The
 *    first version was 16:9 with the gift in the lower-right corner, and this
 *    card is a wide strip - cover took the bottom off and the gift with it;
 *  - the artwork keeps clear of all four edges. A card's width comes from the
 *    phone, so some crop is always possible, and art against the frame is art
 *    that loses an edge on the first handset that rounds differently.
 *
 * The ground is light, so the copy is the dark band green and the button is
 * filled rather than white - white on this would be unreadable.
 */
const REFER_ART = require('../../assets/uploads/banner/refer-earn.png');

/**
 * The referral card's width over its height.
 *
 * ---------------------------------------------------------------------------
 * 3.2, AND THE NUMBER IS NOT A TASTE
 * ---------------------------------------------------------------------------
 * The card stacks three things down its left side - a title, a line of copy
 * and a button - which need about 87pt together once the padding is counted.
 * The card's width is the page less its gutters, so on the narrowest phone
 * this app supports (320pt) that is 280pt, and the height follows from this
 * ratio:
 *
 *   4.4  ->  64pt   the copy clips on a small phone
 *   3.6  ->  78pt   still short of what it needs
 *   3.2  ->  88pt   fits, and 100pt on an ordinary 360pt phone
 *
 * So a wider ratio is not "a slimmer banner", it is text cut off on exactly
 * the handsets least able to spare it. The artwork is what should change to
 * match this, not the other way round.
 *
 * ---------------------------------------------------------------------------
 * 2.9, WHICH IS SLIGHTLY TALLER THAN THE ARTWORK
 * ---------------------------------------------------------------------------
 * The picture is 2241x702, so 3.19. Asking for 2.9 makes the card about ten
 * points taller on an ordinary phone - 100pt to 110pt - and `cover` pays for
 * that by trimming the SIDES rather than the top and bottom.
 *
 * That trim is 9%, so 4.5% off each edge, and the right edge stops at x=2138
 * of 2241. The coins end at roughly x=1900, so nothing in the picture is lost;
 * what goes is empty wall on the left and empty wall on the right - which is
 * exactly the clearance the artwork was asked to keep.
 *
 * Going much past this starts eating into the coins, so if the card needs to
 * be taller again, the picture is what should change.
 */
const REFER_ASPECT = 2.9;
import {
  RestoredApp,
  saveCart,
  saveCartHours,
  saveSession,
  clearAll as clearStoredData,
} from '../data/storage';
import {
  forgetHours,
  hourRange,
  hoursAfterAdd,
  isHourly,
} from '../data/hourly';
import {
  Category,
  CategoryKey,
  guarantees,
  maxPerBooking,
  Service,
  SubCategory,
} from '../data/services';
import { colors, radii, screenPadding, spacing, useResponsive } from '../theme';

/**
 * Home.
 *
 * Top to bottom: where you are, what you can search for, what we are pushing,
 * the categories, then the full catalogue category by category.
 *
 * State here is deliberately thin - a cart of service keys and the selected
 * tab. Everything else is read from `src/data/services.ts`. When the catalogue
 * comes from an API, this screen should keep its shape and only swap where the
 * arrays come from.
 */

/*
 * `RAIL_CARD_RATIO` lived here - 0.42, a share of the screen sized so two
 * cards and the edge of a third were visible, because that peeking third card
 * was the only thing telling anyone a rail scrolled sideways.
 *
 * The only rail left on this screen is the categories one, and it sizes its
 * own tiles. Services are a wrapping grid whose tiles are measured off the
 * page's own width - see `serviceTileWidth` - and that width is what both the
 * compiled block and a published one use. Passing the rail's share to a grid
 * is what put one card on each row with the rest of the row empty.
 */

/**
 * How many category tiles the home page shows before "View all".
 *
 * Six, because `CategoryRail` lays out three across on a phone - so six is two
 * full rows with no ragged last row, and the section ends where a row ends.
 * Four or eight would leave a single tile stranded on the third row on the
 * narrow layouts, which reads as a loading state rather than a deliberate cut.
 */
/**
 * Whether the app says anything after a deletion request reaches the server.
 * **Off**, at Vishal's request.
 *
 * -----------------------------------------------------------------------------
 * WHAT IS HIDDEN, AND WHAT IT COSTS
 * -----------------------------------------------------------------------------
 * The receipt in `runDeleteAccount` is the only place, once the confirm sheet
 * was cut back to "Are you sure?", that says the account keeps working until
 * our team acts and can be brought back afterwards. With this off the tap is
 * silent: the sheet closes and nothing else happens.
 *
 * Two consequences worth naming, because they are what will be reported as
 * bugs:
 *
 *  - Nobody can tell the request landed, so a second tap is likely. Harmless -
 *    the server refuses a duplicate - but that refusal comes back through the
 *    FAILURE branch, which is the one message that still appears.
 *  - Bookings go ahead for up to ninety days with nothing having said they
 *    would, and somebody who believes they are finished may not be there when
 *    a professional arrives.
 *
 * Nothing is deleted. Flip this to `true` and the receipt is back as it was.
 *
 * The failure message is deliberately NOT behind this flag: "Request not sent"
 * is not a receipt, it is the difference between having asked and not having
 * asked, and swallowing it would leave somebody believing they had deleted an
 * account they had not.
 */
const DELETION_RECEIPT_VISIBLE = false;

const HOME_CATEGORY_COUNT = 6;

type Props = {
  /** Where the work happens, chosen in the location flow. */
  address: string;
  /**
   * The pin behind that line, when `App` knows it.
   *
   * The fallback the coverage check needs when the address book cannot
   * supply one - during onboarding, and for anyone signed out, where the
   * book is empty and matching the line against it finds nothing. Without
   * this, coverage stayed permanently unknown for those customers and the
   * coming-soon banner never appeared.
   */
  addressPin?: { lat: number; lng: number };
  /** Picked from the cart's address sheet. */
  onChangeAddress?: (next: string, pin?: { lat: number; lng: number }) => void;
  /**
   * Clears `App`'s address line on sign-out. The book itself needs no
   * equivalent - it lives behind `useAddresses` below, keyed on `signedIn`
   * the same way `useBookings`' list already is, so it clears itself. Without
   * this the LINE stayed on disk regardless of who signed in next - the same
   * bug the book itself used to have, one level up.
   */
  onSignedOut?: () => void;
  /**
   * What was on disk at launch, or null on a first run.
   *
   * Read once as the initial value of the session and the basket - not applied
   * in an effect. `App` does not render this screen until the read has landed,
   * so there is nothing to wait for here.
   */
  restored?: RestoredApp | null;
};

/**
 * Midnight today.
 *
 * `describeSlot` needs a day boundary to compare against, not an instant -
 * given `new Date()` it would compare times as well as dates and a slot later
 * this afternoon would stop reading as "Today".
 */
const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/** The account's sub-pages. `null` is the account list itself. */
type AccountPage =
  | null
  | 'edit-profile'
  | 'settings'
  | 'addresses'
  | 'payment'
  | 'ratings'
  | 'support'
  | 'privacy'
  | 'notifications'
  | 'refer'
  | 'subscription';

function HomeScreen({
  address,
  addressPin,
  onChangeAddress,
  onSignedOut,
  restored,
}: Props) {
  const resp = useResponsive();

  /**
   * What the platform is actually selling.
   *
   * Fetched once here and handed down, rather than each screen importing
   * `data/services` for itself. That file is now the offline fallback: it is
   * what the hook starts with, and what it keeps if the API cannot be reached.
   *
   * The app used to sell 48 services that existed only inside it, of which the
   * platform had heard of two. Everything below reads `catalogue.services`
   * instead, so a price change or a new trade is a server change rather than a
   * release.
   */
  const catalogue = useCatalogue();

  /*
   * What rotates through the search field, from the catalogue rather than the
   * bundled `searchHints`.
   *
   * A hint is a promise that typing it finds something. The compiled five were
   * written for a catalogue this app no longer sells, so every one of them now
   * returns nothing - which reads as search being broken rather than as that
   * service not existing. See `hintsFrom`.
   */
  const hints = useMemo(
    () => hintsFrom(catalogue.services),
    [catalogue.services],
  );

  const { categoryFor } = catalogue;

  const [tab, setTab] = useState<TabKey>('home');
  /**
   * Which tab the cart was opened from, so Back returns to it.
   *
   * ---------------------------------------------------------------------------
   * WHY THIS IS NOT JUST `setTab('home')`
   * ---------------------------------------------------------------------------
   * The cart is reachable from four places - the cart bar on Home, the same bar
   * on Services, "Book again" on a booking, and the basket button on a service
   * page - and Back used to send every one of them to Home. Someone browsing
   * Services who opened the basket, changed their mind and pressed Back lost
   * their place in the catalogue and landed on a tab they had not been on,
   * which reads as the app jumping somewhere at random.
   *
   * Recorded at the moment of opening rather than derived afterwards: once the
   * tab has changed, the tab it changed FROM is gone, and nothing else on this
   * screen remembers it.
   *
   * `'home'` is the initial value rather than null so the cart opened by some
   * future route that forgets to set an origin still has somewhere sensible to
   * go - the old behaviour, kept as the fallback rather than as the rule.
   */
  const [cartOrigin, setCartOrigin] = useState<TabKey>('home');
  /**
   * Where Back from the Bookings tab lands - `cartOrigin`'s twin.
   *
   * The profile's stat strip and its "My Bookings" row both open the
   * Bookings tab; hardware back from there used to land on Home, which is
   * not where the person came from. `'home'` when the tab was reached any
   * other way, so the old behaviour stays the rule and this the exception.
   */
  const [bookingsOrigin, setBookingsOrigin] = useState<TabKey>('home');
  /**
   * Whether the Chat tab was opened from Help & Support's "Chat with us".
   *
   * That button is the only way in while the tab itself is hidden from the
   * bar, and the list it lands on drew no back arrow - it is a tab root - so
   * on an iPhone there was no way back to the page that sent you. True means
   * the list shows an arrow, and back (either kind) returns to Help & Support.
   * Picking any tab from the bar clears it.
   */
  const [chatFromSupport, setChatFromSupport] = useState(false);
  /**
   * Whether the standing cart bar has been dismissed for now.
   *
   * Hides the bar WITHOUT touching the basket - see `CartBar.onDismiss`. The
   * Cart tab keeps its badge and the lines are all still there; this only
   * stops the strip following the customer down every screen once they have
   * read it.
   *
   * Cleared whenever the cart changes, in `changeQuantity`: adding something
   * new is a fresh reason to show the bar, and a dismissal that outlived the
   * basket it was about would leave someone wondering where their confirmation
   * went.
   */
  const [cartBarHidden, setCartBarHidden] = useState(false);
  /**
   * Service key → how many are in the cart.
   *
   * A count rather than a flag, because the card's control becomes a stepper
   * once something is in. A key is dropped entirely when it reaches zero, so
   * "is it in the cart" stays a plain lookup and the map never fills with
   * zeroes.
   */
  const [cart, setCart] = useState<Record<string, number>>(
    restored?.cart ?? {},
  );

  /**
   * How many hours each hourly service in the basket is booked for.
   *
   * Beside the basket rather than inside it - `data/hourly.ts` explains
   * why widening the cart's value would make every basket already saved on a
   * phone unreadable. An entry is only ever written by the picker, and only
   * for a service sold by the hour; everything else reads a missing entry as
   * that service's own minimum.
   */
  const [cartHours, setCartHours] = useState<Record<string, number>>(
    restored?.cartHours ?? {},
  );

  /** The service whose sheet is open, or null. */
  const [openService, setOpenService] = useState<Service | null>(null);

  /*
   * The category drill-down, in two steps.
   *
   * `pickingCategory` is the sheet asking which subcategory; `openSub` is the
   * results page it leads to. They are separate because the sheet closes
   * before the page opens - two modals stacked on Android leave the lower
   * one's scrim over the upper.
   */
  const [pickingCategory, setPickingCategory] = useState<Category | null>(null);
  /**
   * The full-catalogue picker, raised over Home by "View all".
   *
   * A sheet rather than the Services tab. The rail on Home shows the first
   * few categories and "View all" is the rest of that same rail - answering it
   * by switching tabs throws away the page somebody was reading and lands them
   * in a different browse model (a flat list of every service) to answer a
   * question about categories. The sheet keeps Home underneath, and closing it
   * returns to the exact scroll position.
   */
  const [catalogueOpen, setCatalogueOpen] = useState(false);
  const [openSub, setOpenSub] = useState<SubCategory | null>(null);

  /**
   * The search screen. Its own flag rather than a tab, because it is opened
   * from the field on Home and closing it must land back there.
   */
  const [searchOpen, setSearchOpen] = useState(false);

  /** The booking whose detail page is open, or null. */
  const [openBooking, setOpenBooking] = useState<Booking | null>(null);

  /**
   * A conversation opened from a booking rather than from the Chat tab.
   *
   * Separate from `tab`, and deliberately layered over `openBooking` rather
   * than replacing it: messaging your professional is a detour from the
   * booking, so closing it has to land back on the booking and not on a
   * conversation list nobody asked for.
   */
  const [openThread, setOpenThread] = useState<Booking | null>(null);

  /**
   * Which of the account's own pages is open, or null for the account itself.
   *
   * One flag rather than five booleans: they are mutually exclusive by nature -
   * you are on exactly one page - and five booleans make "which is on top" a
   * question with 32 answers, most of them wrong.
   */
  const [accountPage, setAccountPage] = useState<AccountPage>(null);

  /**
   * The order being paid for, or null when nobody is at checkout.
   *
   * The order itself rather than a boolean: it is a snapshot of the basket at
   * the moment "Proceed to Pay" was tapped, and payment has to keep charging
   * for that even if the basket changes underneath it. It also holds the
   * booking reference, which must not be minted twice for one attempt.
   *
   * The cart stays open underneath, so backing out of payment lands on the
   * basket rather than all the way out on Home.
   */
  const [payOrder, setPayOrder] = useState<PaymentOrder | null>(null);
  /**
   * A basket paid straight from the cart, for the receipt `PaymentScreen`
   * opens on. Null for every order that still goes through its summary -
   * cash, recurring.
   */
  const [paidBasket, setPaidBasket] = useState<{
    bookings: Booking[];
    at: Date;
  } | null>(null);
  /**
   * Razorpay is being opened from the cart. The cart's button says so and
   * ignores taps; the ref is the real guard, since two taps can land before
   * the state does and the SDK resolves every open with the same event.
   */
  const [openingPayment, setOpeningPayment] = useState(false);
  const openingPaymentRef = useRef(false);
  /** Set once the pay-from-cart run exists - `onCheckout` is declared above it. */
  const payFromCartRef = useRef<(order: PaymentOrder) => void>(() => {});

  /*
   * Bookings made this session used to be held here, in a `placed` array that
   * survived until the next launch and was known to nobody else.
   *
   * They live on the platform now - see `orders` below - so the array is gone
   * rather than kept alongside as a second, disagreeing copy of the truth.
   */

  /**
   * Reviews, keyed by booking. Seeded, then whatever this session writes.
   *
   * Held here for the same reason the basket is: this screen owns everything
   * that outlives a sub-screen, and the review is read by three of them - the
   * booking detail (which shows it back), the composer (which edits it) and
   * the account's reviews page (which lists them).
   *
   * NOT PERSISTED, like `placed`. A reload loses anything written this
   * session; `src/data/storage.ts` saves the session and the basket only.
   *
   * Starts EMPTY. This used to seed from `seededReviews` - three invented
   * reviews of bookings nobody made ("Excellent service! The team was punctual
   * and very thorough.") - and because the merge below spreads `current` last,
   * those three won against the server every time. So a real account's own
   * reviews were masked by fiction it could not delete, and the empty state
   * was unreachable. The server is the only source now.
   */
  const [reviews, setReviews] = useState<Record<string, MyReview>>({});
  /** The booking whose review composer is open, or null. */
  const [rating, setRating] = useState<Booking | null>(null);

  /** The booking whose cancel sheet is open, and its confirmation. */
  const [cancelling, setCancelling] = useState<Booking | null>(null);
  /** Why a cancellation did not go through, for the sheet that says so. */
  const [cancelFailed, setCancelFailed] = useState<string | null>(null);
  /** The service or category just tapped that is not offered here, by name. */
  const [comingSoonFor, setComingSoonFor] = useState<string | null>(null);

  const [cancelled, setCancelled] = useState<{
    booking: Booking;
    reason?: CancelReason;
  } | null>(null);

  /** The booking whose slot picker is open, and what came of it. */
  const [rescheduling, setRescheduling] = useState<Booking | null>(null);
  /**
   * What the server says about moving the booking whose picker is open.
   *
   * `undefined` while the answer is in flight - the sheet opens immediately
   * on the tap and holds Confirm until this lands, rather than leaving the
   * button dead for a round trip. A refusal never reaches this state: it
   * closes the sheet and becomes `rescheduleFailed`.
   */
  const [allowance, setAllowance] = useState<RescheduleAllowance | undefined>(
    undefined,
  );
  /** Why a reschedule did not go through, for the sheet that says so. */
  const [rescheduleFailed, setRescheduleFailed] = useState<string | null>(null);
  /** The moved booking and its new slot line, for the confirmation. */
  const [rescheduled, setRescheduled] = useState<{
    booking: Booking;
    when: string;
  } | null>(null);

  /** The booking whose tax invoice is open. */
  const [invoiceBooking, setInvoiceBooking] = useState<Booking | null>(null);

  /** The wallet - opened from Home's header, in place of the old cart icon. */
  const [walletOpen, setWalletOpen] = useState(false);

  /**
   * The visit the safety screen was opened from.
   *
   * Always a booking. It used to be `Booking | true | null`, with `true`
   * meaning "opened with no booking behind it" - but nothing ever set it that
   * way: the only producer is `BookingDetailScreen`'s `onSos`, which hands over
   * the booking it is showing. `SosScreen` still takes its `booking` prop
   * optionally, so a booking-less entry point can be added the day one exists;
   * carrying the variant before then only made every read of this state
   * narrow a case that could not happen. Compare `newTicket` below, which keeps
   * its `true` because "Raise a request" genuinely produces one.
   */
  const [sosFor, setSosFor] = useState<Booking | null>(null);

  /**
   * The live trip the alarm was raised from, when it was raised from a car.
   *
   * Separate state from `sosFor` rather than a union, because they are not
   * alternatives to each other in the way that would make a union honest: a
   * customer's ride is a standalone `customer_trip` with no booking, and a
   * booking's alarm has no ride. One screen serves both and takes each as its
   * own optional prop, so a future alert that genuinely has both - a Pro being
   * carried to a job - needs no change here.
   */
  const [sosForRide, setSosForRide] = useState<{
    id: string;
    driverName?: string | null;
    plate?: string | null;
  } | null>(null);

  /**
   * Whether the driver-service page is open.
   *
   * A flag rather than a selected record, unlike every other page in this
   * file: there is exactly one driver service and it is not in the catalogue,
   * so there is nothing to carry into the screen - see `DriverScreen`'s own
   * note on why its content is constants.
   */
  const [driverOpen, setDriverOpen] = useState(false);

  /** Support requests, and which of them is open. */
  const [tickets, setTickets] = useState<Ticket[]>(seededTickets);
  const [openTicket, setOpenTicket] = useState<Ticket | null>(null);
  /** The requests list itself, reached from Help & Support. */
  const [ticketsOpen, setTicketsOpen] = useState(false);
  /**
   * The new-request form: `true` for a plain ticket, a booking when it was
   * opened as "dispute this job".
   */
  const [newTicket, setNewTicket] = useState<Booking | true | null>(null);

  const [notificationsOpen, setNotificationsOpen] = useState(false);

  /**
   * The address book, raised from the header's location chip.
   *
   * The chip used to jump to the profile tab, which answered a different
   * question: it took someone who wanted to switch between two saved addresses
   * and dropped them on the account page to find the address book themselves.
   * A sheet keeps them where they were - Home does not unmount, so the scroll
   * position, the cart and any open category survive the change.
   *
   * It lives in `sheets` rather than on the Home branch because the same chip
   * is on the cart's header, and a sheet that exists on one branch is a
   * control that silently does nothing everywhere else.
   */
  const [addressOpen, setAddressOpen] = useState(false);
  /*
   * What to do once the address sheet has actually gone - the same fix, for
   * the same reason, as the cart's own `afterAddressSheet`. Signed out, "Add
   * new" opens sign-in; opened in the tick this sheet closed, iOS never showed
   * it and the app stopped taking touches.
   */
  const afterAddressSheet = useRef<(() => void) | null>(null);
  const leaveAddressSheetFor = useCallback((next: () => void) => {
    afterAddressSheet.current = next;
    setAddressOpen(false);
  }, []);
  const runAfterAddressSheet = useCallback(() => {
    const next = afterAddressSheet.current;
    afterAddressSheet.current = null;
    next?.();
  }, []);
  const [notifications, setNotifications] =
    useState<AppNotification[]>(initialNotifications);
  const unreadCount = notifications.filter(item => !item.read).length;

  /*
   * The session, seeded from disk rather than from a constant.
   *
   * `restored` is read once, as the initial value - not applied in an effect.
   * An effect would render one frame signed out before correcting itself, and
   * that frame is long enough to show the wrong header and to let a gated tap
   * raise the auth sheet at someone already signed in.
   */
  const [signedIn, setSignedIn] = useState(restored?.session.signedIn ?? false);
  const [authOpen, setAuthOpen] = useState(false);
  /** The verified number, digits only. Null until sign-in completes. */
  const [phone, setPhone] = useState<string | null>(
    restored?.session.phone ?? null,
  );
  /** Set from Edit profile; OTP sign-up never captures a name. */
  const [profileName, setProfileName] = useState<string | null>(
    restored?.session.name ?? null,
  );
  const [profileEmail, setProfileEmail] = useState<string | null>(
    restored?.session.email ?? null,
  );
  /**
   * The optional profile answers - see `PersonalInfoScreen`.
   *
   * Not restored from the persisted session: that record carries the name and
   * email only, and these arrive with `GET /auth/me` once the backend returns
   * them. Null until then, which the screen shows as no chip selected - the
   * same as never having been asked, which is exactly what it is.
   */
  const [profileHousehold, setProfileHousehold] =
    useState<auth.HouseholdType | null>(null);
  const [profileGender, setProfileGender] = useState<auth.Gender | null>(null);
  /** Local URI of the chosen picture, or null for the initial. */
  const [profilePhoto, setProfilePhoto] = useState<string | null>(
    restored?.session.photo ?? null,
  );

  /**
   * Signed in on an account that has been closed.
   *
   * ---------------------------------------------------------------------------
   * WHY THAT IS A REAL STATE AND NOT A CONTRADICTION
   * ---------------------------------------------------------------------------
   * Deleting an account never retires the phone number, so the OTP still
   * reaches the same account and the session still opens. Every route on the
   * server but three then answers 403 - the session exists so the app can say
   * "this account was deleted" and offer to bring it back.
   *
   * Set from the sign-in response, which is the only place it can come from:
   * closing an account revokes every session, so the stored one dies, the app
   * signs out on the next 401, and the way back in is the OTP sheet.
   *
   * Not persisted. A relaunch re-reads it by signing in, and a stale `true` on
   * disk would show this screen to somebody whose account is open.
   */
  const [accountDeletedAt, setAccountDeletedAt] = useState<string | null>(null);

  /**
   * The customer's bookings, from the platform.
   *
   * Declared here, below `signedIn`, because it is the sign-in state that
   * decides whose list this is - and the hook re-reads when that flips, which
   * is what makes signing in show your bookings without a manual refresh.
   *
   * It never falls back to the seed data: an empty list is a real answer, and
   * showing somebody else's example bookings as yours is a lie about a
   * professional turning up at an address.
   */
  const orders = useBookings(
    signedIn,
    catalogue.services,
    key => categoryFor(key).label,
  );

  /**
   * The customer's saved addresses, from the platform - same reasoning as
   * `orders` above, and declared right beside it for the same reason: both
   * need `signedIn` to know whose data this is, and both go empty rather
   * than showing someone else's the moment it flips to false.
   */
  const addressBook = useAddresses(signedIn);

  /**
   * The row behind the address line the header is showing.
   *
   * `address` is only ever a formatted string - see `App`, which owns it -
   * so the row has to be recovered the same way `onCreateBooking` recovers
   * the one to book against: by matching that line to the one entry in the
   * book that formats to it.
   */
  const selectedAddress = addressBook.addresses.find(
    saved => formatAddress(saved) === address,
  );

  /**
   * What the PLATFORM said about that address, if it ever said.
   *
   * `useCoverage` re-derives coverage from the pin, which is right for an
   * address just dropped on the map and redundant for one the server has
   * already ruled on. A locally-kept address carries that ruling on the row
   * itself - see `SavedAddress.serviceable` - so this reads it directly
   * rather than waiting for a geocode to come back and agree.
   *
   * It also closes a gap the geocode cannot: `useCoverage` answers `null`
   * until both `/cities` and the reverse geocode land, and during that window
   * an address we ALREADY know is out of area would pass the checkout gate.
   */
  const knownUnserviceable = selectedAddress?.serviceable === false;

  /**
   * The pin behind the address line Home is showing, for `useCoverage`.
   *
   * `address` is only ever a formatted string - see `App`, which owns it -
   * so the coordinates have to be recovered the same way `onCreateBooking`
   * recovers the row to book against: by matching the line the header shows
   * to the one entry in the book that formats to it.
   */
  /*
   * The book's pin when there is one, `App`'s otherwise.
   *
   * This fallback is the whole reason coverage works for a signed-out
   * customer. `selectedAddress` matches the header LINE against the address
   * book, and that book is empty during onboarding and for anyone not signed
   * in - so the lookup found nothing, there were no coordinates, and
   * `useCoverage` answered `null` (unknown) forever. An address in Mumbai
   * looked exactly like one in Indore: no banner, no message, no block.
   *
   * The book still comes first. Its row is the one the booking will actually
   * travel on, and it is the copy that gets repaired when an address is
   * edited, so a stale pin in `App` must never win over it.
   */
  const activeAddressCoords = selectedAddress?.coords ?? addressPin;

  /**
   * US-2.6 - told before picking a service, not after.
   *
   * `null` while unknown (no address yet, or the checks have not landed) and
   * while covered - both draw nothing. See `CoverageBanner` and `useCoverage`
   * for why "not covered" is the only state worth a banner at all.
   */
  /*
   * `NO_ADDRESS` is the "Set your location" prompt, not a place - geocoding it
   * would ask where "Set your location" is and get an answer somewhere. Passed
   * as undefined so the hook stays silent until there is a real address.
   */
  /*
   * `useServiceCity` rather than `useCoverage`: the same hook and the same two
   * requests, but it also hands back WHICH city answered. The published home
   * configuration is targeted per city and this screen had no id to ask with -
   * see the fetch further down.
   */
  const { covered: coverage, city: serviceCity } = useServiceCity(
    activeAddressCoords,
    address === NO_ADDRESS ? undefined : address,
  );

  /*
   * ---------------------------------------------------------------------------
   * WHAT IS OFFERED AT THIS ADDRESS, SERVICE BY SERVICE
   * ---------------------------------------------------------------------------
   * Coverage above is the CITY. Within a covered city, ops assigns each service
   * to the areas it is actually run in - so a customer in Indore can still be
   * somewhere a given service has not reached. This asks the server for that,
   * for the same pin, and the answer greys those services out everywhere they
   * are drawn.
   *
   * Only while the city's area gate is on: with it off a booking goes through
   * anywhere, and greying would say "coming soon" about something bookable
   * today. The server reads the gate; see `services/areaAvailability.ts`.
   *
   * The city is sent so a pin that lands between two areas is judged against
   * its own city's gate.
   */
  const areaServices = useUnavailableServices(
    activeAddressCoords,
    serviceCity?.id,
  );
  const reloadAreaServices = areaServices.reload;

  /**
   * The same answer collapsed to the one state worth acting on.
   *
   * Kept beside `coverage` rather than replacing it: the banners and the
   * checkout gate only ever care about "definitely not covered", while
   * `ServicesScreen` is handed the raw three-state value so it can stay
   * silent on `null` for itself.
   */
  const outOfCoverage = coverage === false || knownUnserviceable;

  /**
   * The request came back and brought nothing.
   *
   * Both halves matter. Without `!loading` this fires on the first frame of
   * every launch, because the catalogue now starts empty - which is the whole
   * point of the change, and would turn an ordinary launch into an error
   * screen for a moment. Without the length check it would fire on a slow
   * connection that is about to succeed.
   *
   * Read as "we could not reach the catalogue", not "there is nothing to
   * sell": a live catalogue with zero services is not a state the platform
   * produces, and if it ever did, an empty shop and a failed request want
   * different sentences anyway.
   */
  const catalogueFailed =
    !catalogue.loading && catalogue.categories.length === 0;

  /**
   * What the Services tab is told, and whether it is showing the message.
   *
   * Computed once here rather than twice at the two places that need it -
   * the screen's own prop and the cart bar under it - so the bar cannot
   * disagree with the screen it sits below.
   */
  const servicesCoverage = knownUnserviceable ? false : coverage;
  const servicesBlocked = servicesCoverage === false;

  /**
   * Adding an address, as an overlay over whatever screen is already open.
   *
   * Reached from the cart's address sheet and from Saved Addresses, so it
   * has to return to either one - an overlay does that for free, because
   * nothing underneath it ever unmounts. `sheets` renders it in every
   * branch below for exactly that reason: wherever "Add new" was tapped
   * from is still there when it closes.
   */
  const [addingAddress, setAddingAddress] = useState(false);
  /*
   * The address "Edit" opened, or null when this is a plain add.
   *
   * The two share one screen - editing IS the location flow, re-picking the
   * point - so what separates them is only whether there is an existing row to
   * write back to. Held beside `addingAddress` rather than folded into it so
   * the add path keeps its `false | true` shape.
   */
  const [editingAddress, setEditingAddress] = useState<SavedAddress | null>(
    null,
  );

  /**
   * Everything bookable-past: this session's, then the seeded ones.
   *
   * Only for id lookups - "the review on the account page names booking X,
   * open its composer". The lists themselves still read `bookingsFor`, which
   * is what filters them.
   */
  const allBookings = useMemo<Booking[]>(
    () => [...orders.bookings, ...seededBookings],
    [orders.bookings],
  );

  /**
   * The four numbers on the account header, counted from real rows.
   *
   * ---------------------------------------------------------------------------
   * `orders.bookings`, NOT `allBookings`
   * ---------------------------------------------------------------------------
   * `allBookings` folds in the bundled examples so a review can still find the
   * booking it names. Counting those would put a stranger's four visits on your
   * profile, which is the same lie `useBookings` refuses to tell by never
   * falling back to seed data. The Bookings tab reads `orders.bookings` for
   * exactly this reason; these numbers read the same list, so the header and
   * the tab can never disagree.
   *
   * A cancelled booking still counts under "Bookings" - you made it - but only
   * `completed` counts as completed. `recurring` and `live` are work that has
   * not happened yet.
   *
   * Reviews are counted off `reviews` rather than `booking.review`, because
   * that record is the merged truth: the server's reviews are folded into it
   * by the effect above, and anything rated this session is in it before the
   * refetch lands. Keys are filtered against the real list so a seeded review
   * about a seeded booking cannot inflate the count.
   *
   * Rating is the mean of the stars actually given, to one decimal - this is
   * the customer's own average, not a score anyone gave them. With nothing
   * rated there is no average to state, so it shows an em dash rather than
   * "0.0", which would read as a bad rating rather than as no rating.
   */
  const accountStats = useMemo<Stat[]>(() => {
    const mine = new Set(orders.bookings.map(booking => booking.id));
    const completed = orders.bookings.filter(
      booking => booking.status === 'completed',
    ).length;

    const stars = Object.values(reviews)
      .filter(review => mine.has(review.bookingId) && review.stars > 0)
      .map(review => review.stars);
    const average =
      stars.length > 0
        ? (stars.reduce((sum, value) => sum + value, 0) / stars.length).toFixed(
            1,
          )
        : '-';

    /*
     * Each number now leads to the thing it counts - the two booking figures
     * to the Bookings tab, the two review figures to the ratings page. The
     * origin is recorded first so hardware back returns to the profile the
     * tap came from rather than to Home.
     */
    const openBookings = () => {
      setBookingsOrigin('profile');
      setTab('bookings');
      orders.refresh().catch(() => {
        /* The hook records the failure and the list shows it. */
      });
    };
    const openRatings = () => setAccountPage('ratings');

    return [
      {
        label: 'Bookings',
        value: String(orders.bookings.length),
        icon: 'calendar',
        onPress: openBookings,
      },
      {
        label: 'Completed',
        value: String(completed),
        icon: 'checkCircle',
        onPress: openBookings,
      },
      { label: 'Rating', value: average, icon: 'star', onPress: openRatings },
      {
        label: 'Reviews',
        value: String(stars.length),
        icon: 'chat',
        onPress: openRatings,
      },
    ];
  }, [orders, reviews]);

  /*
   * Keeps the open booking in step with the platform, not just its own
   * memory.
   *
   * `openBooking` is handed a `Booking` snapshot at the moment it opens -
   * from the list, a notification, a receipt - and after that it is a copy,
   * not a live view. Cancelling, rating and refreshing all update
   * `orders.bookings` correctly; none of them updates a snapshot nobody told.
   * This effect is what reconciles the two, so the detail screen's refresh
   * button - and anything else that changes the underlying list - actually
   * reaches the screen that is open.
   */
  useEffect(() => {
    if (!openBooking) {
      return;
    }
    const updated = orders.bookings.find(item => item.id === openBooking.id);
    if (updated && updated !== openBooking) {
      setOpenBooking(updated);
    }
  }, [orders.bookings, openBooking]);

  /*
   * Pull the DETAIL for whichever booking is open.
   *
   * `GET /bookings` - the only call the list makes - does not send a
   * `timeline`, and `startedAt` is read off the `started` event in it. So a
   * booking that has only been through the list has no start instant, and the
   * running clock on `BookingDetailScreen` has nothing to count from: that is
   * why the in-progress panel showed "Started at" with nothing after it.
   *
   * Keyed on the ID rather than the object. The reconcile effect above swaps
   * `openBooking` for a new object every time the list reloads, and depending
   * on the object would refetch on each of those - an endless loop, since the
   * fetch updates the list that produces the new object.
   */
  const openBookingId = openBooking?.id;
  const refreshOne = orders.refreshOne;

  useEffect(() => {
    if (!openBookingId) {
      return;
    }
    refreshOne(openBookingId).catch(() => {});
  }, [openBookingId, refreshOne]);

  /**
   * A fresh end-of-service code, when the professional's app will not take the
   * one on screen.
   *
   * Refetches straight afterwards rather than trusting a response: the route
   * returns nothing, and the new digits reach this app only on the next read
   * of the booking. `refreshOne` rather than the whole list, because the code
   * lives on the detail - the list has never carried it.
   *
   * Deliberately NOT caught here. The detail screen prints the reason under
   * the button, which is the right place for it: the customer is standing next
   * to the professional and needs the code still on screen while they read
   * what went wrong.
   */
  const resendEndCode = useCallback(
    async (booking: Booking) => {
      await resendEndOtp(booking.id);
      await orders.refreshOne(booking.id);
    },
    [orders],
  );

  /** Pull-to-refresh on `BookingDetailScreen` - a real refetch. */
  const refreshBooking = useCallback(() => {
    orders.refresh().catch(() => {});
    /* The list alone would drop `startedAt` again - it has no timeline. */
    if (openBookingId) {
      orders.refreshOne(openBookingId).catch(() => {});
    }
  }, [orders, openBookingId]);

  /**
   * Pay for a booking that was placed and then left unpaid.
   *
   * An online booking is created before the gateway opens - the server mints
   * the Razorpay order from the booking’s own amount and has nothing to mint
   * it against until the booking exists - so backing out of the sheet leaves
   * a real, unpaid booking behind. It is released after the hold window, and
   * until this existed the only way to rescue it was to book the whole thing
   * again.
   *
   * The same `payForBookings` the checkout flow runs, so the recovery here
   * is the same recovery there: a booking the webhook already settled is
   * stepped over rather than charged twice, and a failure that might still
   * have taken money says so instead of claiming it did not.
   */
  const payForBooking = useCallback(
    (booking: Booking) => {
      payForBookings([booking.id])
        .then(outcome => {
          /* Refresh either way: a partial or failed run still moves the
             booking, and the screen must not keep showing yesterday. */
          refreshBooking();

          if (outcome.status === 'paid') return;
          if (outcome.status === 'cancelled') return;

          notify(
            outcome.uncertain
              ? 'We could not confirm your payment'
              : 'Payment failed',
            outcome.uncertain
              ? 'It may still have gone through. Check this booking again in a minute before paying once more.'
              : /* Not `outcome.message` - that is the SDK’s or the API’s own
                 wording, written for a developer. A code in place of an
                 explanation reads as a broken app rather than a refused
                 card. Support can read the real reason in Razorpay. */
                'Nothing has been charged. Please try again.',
          );
        })
        .catch(() => {
          refreshBooking();
          notify(
            'Could not open payment',
            'Something went wrong before the gateway opened. Nothing has been charged.',
          );
        });
    },
    [refreshBooking],
  );

  /*
   * A booking in motion keeps itself fresh - nobody should have to pull.
   *
   * The app has no push for booking-state changes, so while anything is live
   * (assigned, on the way, working) the list re-reads itself on a slow poll,
   * and the open detail refetches with it so its OTP panel, timer and stepper
   * move on their own. Thirty seconds is a compromise: fast enough that
   * "arrived" appears while it is still true, slow enough to cost nothing.
   *
   * Depends on the two stable callbacks off the hook, not on `orders` itself,
   * whose identity changes every render - that would rebuild the interval on
   * each render and it would never get to fire.
   */
  const anyBookingLive = orders.bookings.some(item => item.status === 'live');
  const refreshList = orders.refresh;
  const refreshOneBooking = orders.refreshOne;

  /* The rating prompt, put away for this session. One id, because only one
     booking is ever asked about at a time. */
  const [ratingHidden, setRatingHidden] = useState<string | null>(null);

  /**
   * What the Home bar is about: the job that just finished and has not been
   * rated, and every job happening now. See `pickBarBookings` for the order
   * and for why the rating card is no longer a fallback behind the live ones.
   */
  const liveBarBookings = useMemo<Booking[]>(
    () =>
      pickBarBookings(orders.bookings, {
        ratedIds: reviews,
        hiddenId: ratingHidden,
      }),
    [orders.bookings, ratingHidden, reviews],
  );

  /*
   * The running clock needs `startedAt`, and only the booking's own read
   * carries it - the list has no timeline on it. So the bar asks for the one
   * booking it is showing, once, when it starts work.
   */
  const runningBarIds = liveBarBookings
    .filter(item => item.stage === 'service' && !item.startedAt)
    .map(item => item.id)
    .join(',');

  useEffect(() => {
    if (!runningBarIds) {
      return;
    }
    for (const id of runningBarIds.split(',')) {
      refreshOneBooking(id).catch(() => {});
    }
  }, [runningBarIds, refreshOneBooking]);

  useEffect(() => {
    if (!signedIn || !anyBookingLive) {
      return;
    }
    const timer = setInterval(() => {
      refreshList().catch(() => {});
      if (openBookingId) {
        refreshOneBooking(openBookingId).catch(() => {});
      }
    }, 30 * 1000);
    return () => clearInterval(timer);
  }, [anyBookingLive, openBookingId, refreshList, refreshOneBooking, signedIn]);

  /**
   * The same news, without waiting for the next tick of that interval.
   *
   * -------------------------------------------------------------------------
   * WHY THE POLL ABOVE IS STILL THERE
   * -------------------------------------------------------------------------
   * This is an accelerator, not a replacement. The thirty seconds above is the
   * floor on how stale a live booking can get on ANY network; the socket makes
   * the common case instant. A hotel wifi that eats WebSockets then costs the
   * customer the speed and nothing else, which is why neither cadence below
   * was loosened to pay for this.
   *
   * It matters most on the screen somebody is actually staring at: "Finding
   * your professional" became a name the moment dispatch found one, rather
   * than up to half a minute later for no reason the customer could see.
   *
   * `refreshOne`, not `refresh`: the server sends which booking moved, and
   * re-reading that one merges it into the same list the whole tab draws from.
   * Pulling the entire list because one row changed would be a bigger request,
   * more often, for less.
   */
  const liveBookingIds = useMemo(
    () =>
      orders.bookings
        .filter(item => item.status === 'live')
        .map(item => item.id),
    [orders.bookings],
  );

  useBookingEvents(liveBookingIds, signedIn, bookingId => {
    refreshOneBooking(bookingId).catch(() => {
      /* Silent, like the poll's own failures: the row on screen is still
         correct in every other respect, and the next tick tries again. */
    });
  });

  /**
   * Stores one review and sends it, wherever it came from.
   *
   * Two things write reviews now - the composer sheet, and the stars on the
   * booking detail, which submit a bare rating on the tap. They must agree
   * about where it is kept and what happens when the send fails, so they
   * share this rather than each carrying a copy of it.
   *
   * Kept locally as well as sent. Everything now reaches the platform -
   * rating, words, tags and photographs - and the local copy is what the
   * list draws until the refetch lands, so a review does not blink out of
   * the screen it was just written on.
   */
  const saveReview = useCallback(
    (review: MyReview) => {
      setReviews(current => ({ ...current, [review.bookingId]: review }));

      orders
        .rate(review.bookingId, {
          rating: review.stars,
          comment: review.comment || undefined,
          tags: review.tags,
          /*
           * The photographs go too, which they never did. The composer used to
           * collect them into local state and stop there - a note here said
           * they "have nowhere to upload to yet", and the platform had been
           * ready for them the whole time. Every picture a customer attached
           * lived on their handset and died with the next reinstall.
           *
           * Hidden ones are left behind: taking a photo down is the one thing
           * on a review that is meant to be undoable, and re-uploading one the
           * customer just removed would undo their own decision.
           */
          photos: visiblePhotos(review).map(photo => ({ uri: photo.uri })),
        })
        .catch(failure => {
          /* Reviews are not worth an error screen, but they are worth not
             lying about - the sheet has already closed, so this says what
             happened rather than leaving a review nobody received. */
          setCancelFailed(
            failure instanceof Error
              ? `Your review was not saved. ${failure.message}`
              : 'Your review was not saved.',
          );
        });
    },
    [orders],
  );

  /**
   * A rating from the stars on the booking detail - no sheet, no writing.
   *
   * Anything already reviewed is kept: somebody correcting four stars to five
   * on a review they wrote last week means to change the number, not to throw
   * away their own sentence, their tags and their photographs.
   */
  const quickRate = useCallback(
    (booking: Booking, stars: number) => {
      const existing = reviews[booking.id];
      saveReview({
        bookingId: booking.id,
        service: booking.title,
        icon: existing?.icon ?? 'star',
        when: booking.when,
        stars,
        comment: existing?.comment ?? '',
        tags: existing?.tags ?? [],
        photos: existing?.photos ?? [],
        edited: !!existing,
      });
    },
    [reviews, saveReview],
  );

  /**
   * Folds the platform's own reviews into `reviews`, so `RatingsScreen` and
   * the booking detail show what was actually left rather than only what was
   * rated this session.
   *
   * `current` wins over the server on a key both have, on purpose. The
   * local copy holds the picked file URIs, and the server answers with
   * short-lived signed GETs for the same pictures - both are correct, and
   * the local ones are already decoded and on screen. Taking the server's
   * would blank the strip for as long as the fetches take, on a review the
   * customer is still looking at.
   *
   * A review from a DIFFERENT session, or another device on the same
   * account, has no local copy to lose, so the server's is simply adopted -
   * which is what makes it show up here at all.
   */
  useEffect(() => {
    const fromServer: Record<string, MyReview> = {};
    orders.bookings.forEach(booking => {
      if (!booking.review) {
        return;
      }
      fromServer[booking.id] = {
        bookingId: booking.id,
        service: booking.title,
        icon: categoryFor(booking.category).icon,
        when: booking.when,
        stars: booking.review.stars,
        comment: booking.review.comment ?? '',
        tags: booking.review.tags,
        photos: booking.review.photoUrls.map((uri, i) => ({
          id: `${booking.id}-${i}`,
          uri,
        })),
      };
    });
    if (Object.keys(fromServer).length === 0) {
      return;
    }
    setReviews(current => ({ ...fromServer, ...current }));
  }, [orders.bookings, categoryFor]);

  /**
   * The account on the server, and the credentials for reaching it.
   *
   * Seeded from disk like the rest of the session, and pushed into the API
   * client by the effect below - the client is a plain module, not a hook, so
   * something has to hand it the token on every launch.
   */
  const [userId, setUserId] = useState<string | null>(
    restored?.session.userId ?? null,
  );
  const [accessToken, setAccessToken] = useState<string | null>(
    restored?.session.accessToken ?? null,
  );
  const [refreshToken, setRefreshToken] = useState<string | null>(
    restored?.session.refreshToken ?? null,
  );

  /**
   * The log-out confirmation, before anything is actually cleared.
   *
   * Logging out used to happen on the bare row tap, and the only visible
   * result was the Log out row disappearing - no confirmation, no statement
   * of what happens next. The sheet says both: that the session ends, and
   * that browsing continues as a guest.
   */
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /**
   * Picking a profile picture, and keeping it.
   *
   * ---------------------------------------------------------------------------
   * THE UPLOAD IS NEW; THE PICKING IS NOT
   * ---------------------------------------------------------------------------
   * This used to end at `setProfilePhoto(uri)` - a `file://` path into this
   * install's own cache, written to local storage and nowhere else. It showed
   * correctly and then vanished on the next reinstall, with nothing on the
   * server to restore it from and nothing for a Pro at the door or an admin on
   * a support call to see.
   *
   * The local URI is still set first, and deliberately: the picture appears
   * the instant it is chosen rather than after a round trip, and the upload
   * catches up behind it.
   *
   * A failed upload is not reported. The customer has done everything right,
   * the picture is on screen, and an error sheet would be telling them off for
   * a network they cannot control - the only cost is that it does not follow
   * them to a new phone, which they will not notice today. A cancel and a
   * failure are the same thing for the same reason.
   */
  const pickPhoto = useCallback(async () => {
    const result = await launchImageLibrary({
      mediaType: 'photo',
      selectionLimit: 1,
      // Downscaled on the way in. The avatar is never drawn above 62pt, and a
      // 12-megapixel original would sit in the cache for the life of the app -
      // and would be what we uploaded.
      maxWidth: 512,
      maxHeight: 512,
      quality: 0.8,
    }).catch(() => null);

    const asset = result?.assets?.[0];
    const uri = asset?.uri;
    if (!uri) {
      return;
    }

    setProfilePhoto(uri);

    /*
     * Behind the local preview. The key is not kept in app state - the URL
     * this becomes is signed and short-lived, so it is fetched when it is
     * needed rather than cached into a session that outlives it.
     */
    uploadProfilePhoto(uri, contentTypeFor(asset)).catch(() => undefined);
  }, []);

  /**
   * The picture on the account, once there is an account to ask about.
   *
   * ---------------------------------------------------------------------------
   * WHAT MAKES A REINSTALL SURVIVABLE
   * ---------------------------------------------------------------------------
   * The local `file://` path restored from storage is valid only inside the
   * install that wrote it, so on a fresh install - or a new phone - it points
   * at nothing. This is the half that fetches the real one back.
   *
   * Signed and short-lived, so it is asked for per session rather than written
   * into storage: a URL that expires has no business being persisted, and a
   * stale one renders as a broken image rather than as no image.
   *
   * A null answer is the ordinary case, not a failure - most customers never
   * set a picture, and `fetchPhotoUrl` already folds the 404 into it. Whatever
   * is on screen is left alone in that case, because a locally-picked photo
   * whose upload has not landed yet is still the customer's photo.
   */
  useEffect(() => {
    if (!signedIn) {
      return;
    }

    const controller = new AbortController();
    fetchPhotoUrl(controller.signal)
      .then(url => {
        if (url) {
          setProfilePhoto(url);
        }
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [signedIn]);

  /**
   * What the user was trying to do when they hit the sign-in wall, replayed
   * once they verify. Without this, signing in succeeds and then nothing
   * happens - the tap that triggered it is lost and has to be repeated.
   */
  const pending = useRef<(() => void) | null>(null);

  /**
   * The order held back because the account had no name yet.
   *
   * The same idea as `pending` above, for the second wall a first-time
   * customer meets: signing in gets them past auth, and then checkout needs a
   * name to put on the booking. Parked here so saving the profile resumes
   * payment rather than leaving them on the account page wondering where
   * their basket went. See `onCheckout`.
   */
  const pendingOrder = useRef<PaymentOrder | null>(null);

  /**
   * Where the location flow opens when it is next raised.
   *
   * ---------------------------------------------------------------------------
   * WHY THIS IS STATE AND NOT A CONSTANT PROP
   * ---------------------------------------------------------------------------
   * `initialStage` used to be the literal `"search"` on the one call site, so
   * every route into the flow - Add Address, the header's chip, an empty cart
   * - landed on a text box. Adding an address now starts on the MAP, and the
   * cart's sheet has a second entry that starts on a GPS fix, so the stage is
   * whatever the thing that opened it asked for rather than one answer for
   * all of them.
   *
   * `'fetching'` is safe to enter directly: that effect raises the permission
   * dialog itself now - see `LocationScreen`. It was not, which is why the
   * only way in used to be a row inside the search screen.
   */
  const [addressStage, setAddressStage] = useState<
    'permission' | 'fetching' | 'search'
  >('fetching');

  /*
   * Mirror the session and the basket to disk whenever either moves.
   *
   * Effects rather than writes at each call site: sign-in, sign-out, editing a
   * profile and six different places that change a quantity would each have to
   * remember, and the one that forgot would be a bug nobody sees until the app
   * is killed. The writes are fire-and-forget - see `storage.ts`.
   */
  useEffect(() => {
    saveSession({
      signedIn,
      phone,
      name: profileName,
      email: profileEmail,
      photo: profilePhoto,
      userId,
      accessToken,
      refreshToken,
    });
  }, [
    accessToken,
    phone,
    profileEmail,
    profileName,
    profilePhoto,
    refreshToken,
    signedIn,
    userId,
  ]);

  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  useEffect(() => {
    saveCartHours(cartHours);
  }, [cartHours]);

  /*
   * The call machine, which lives above this screen in `App.tsx`.
   *
   * Optional on purpose - see `useVoiceCallOptional`. This screen only ever
   * forwards a press to it and has nothing of its own to do with calling.
   */
  const voice = useVoiceCallOptional();

  /**
   * Runs `action` if signed in; otherwise parks it and raises the auth sheet.
   *
   * Every gated affordance goes through here rather than checking `signedIn`
   * at each call site, so there is one place that decides what needs an
   * account.
   */
  const gated = useCallback(
    (action: () => void) => {
      if (signedIn) {
        action();
        return;
      }
      pending.current = action;
      setAuthOpen(true);
    },
    [signedIn],
  );

  /**
   * Opens the wallet, asking for a sign-in first when there is not one.
   *
   * ---------------------------------------------------------------------------
   * A SIGNED-OUT WALLET IS A WALLET FULL OF NOTHING
   * ---------------------------------------------------------------------------
   * The balance, the passbook and the referral credits all belong to an
   * account, and every one of them is read with a token. Signed out the screen
   * still opened - it just showed ₹0, an empty history and no way to tell that
   * the zero was "you have no money" rather than "we do not know who you are".
   *
   * Six places open this - Home's header, the account row, and the desktop
   * hamburger in four layout branches - so the gate lives here rather than at
   * each of them. `gated` parks the open and raises sign-in; verifying runs
   * the parked action, so the wallet lands by itself afterwards.
   */
  const openWallet = useCallback(() => {
    gated(() => setWalletOpen(true));
  }, [gated]);

  /**
   * Signed in for real: tokens issued, account known.
   *
   * `AuthSheet` has already pushed the access token into the API client, so
   * requests fired by the parked action below go out authenticated. What
   * happens here is the durable half - putting it in state so the effect
   * above writes it to disk, and so a cold start comes back signed in.
   *
   * The server's `name` is taken only when it has one. A returning customer
   * who set a name keeps it; a brand-new account has none, and overwriting
   * whatever this device already had with `null` would wipe a name the
   * customer typed before auth was real.
   */
  const onVerified = useCallback((session: AuthSession) => {
    setSignedIn(true);
    setPhone(session.user.phone);
    setUserId(session.user.id);
    setAccessToken(session.accessToken);
    setRefreshToken(session.refreshToken);

    if (session.user.name) {
      setProfileName(session.user.name);
    }
    if (session.user.email) {
      setProfileEmail(session.user.email);
    }
    /* Undefined until the backend returns these, which reads as null - no
       answer - rather than overwriting anything. */
    setProfileHousehold(session.user.householdType ?? null);
    setProfileGender(session.user.gender ?? null);

    /* A closed account signs in like any other. This is what makes the app
       draw the deleted screen instead of the home page. */
    setAccountDeletedAt(session.user.deletedAt ?? null);

    setAuthOpen(false);
    const action = pending.current;
    pending.current = null;
    action?.();
  }, []);

  /**
   * Clears the session but keeps the cart, which is local either way.
   *
   * The address BOOK needs no help here: `addressBook` is keyed on `signedIn`
   * and clears itself the moment this flips to false, same as `orders`. The
   * one line `App` still owns - the address currently in use - does not
   * follow `signedIn` on its own, so `onSignedOut` is called to clear that.
   */
  const onSignOut = useCallback(() => {
    setSignedIn(false);
    setPhone(null);
    /* The name and email go too. They are the signed-out person's, and left
       behind they would greet whoever signs in next by the wrong name. The
       effect below writes the cleared session straight to disk. */
    setProfileName(null);
    setProfileEmail(null);
    setProfilePhoto(null);
    /* Belongs to the account that just left. Kept, it would show the deleted
       screen to whoever signs in next on this handset. */
    setAccountDeletedAt(null);

    /* The credentials, and the client's copy of them. Clearing state alone
       would leave the module still holding the token, so the next request
       would go out signed in as someone who has just signed out. */
    setUserId(null);
    setAccessToken(null);
    setRefreshToken(null);
    setClientToken(null);

    /* And the live socket, which authenticated with the token just cleared.
       Left open it would keep pushing that customer's bookings into an app
       that no longer belongs to them - and it would try to re-authenticate on
       its next reconnect with nothing to send. */
    closeLiveBookings();

    onSignedOut?.();
  }, [onSignedOut]);

  /**
   * Ask for the account to be deleted, and stay signed in.
   *
   * -------------------------------------------------------------------------
   * IT USED TO SIGN THE PERSON OUT HERE. THAT WAS WRONG
   * -------------------------------------------------------------------------
   * `DELETE /auth/me` deletes nothing - it records a request. Our team acts on
   * it, or it goes through on its own after ninety days.
   *
   * So this used to clear the session, wipe the local copy and say "account
   * deleted" over an account that was still perfectly live: still holding
   * bookings, still able to have a professional arrive at the door. Somebody
   * with a job the next morning would have been told they had no account and
   * then had somebody turn up anyway.
   *
   * Nothing local is touched now. The session stays, the addresses and cart
   * stay, and the message says what actually happened. The sign-out comes
   * later, from the server, when the request is accepted - every device is
   * dropped at once, and the next launch lands on the deleted-account screen
   * with the button to bring it back.
   *
   * A failure says so plainly and leaves everything alone.
   */
  const runDeleteAccount = useCallback(async () => {
    setDeleting(true);
    try {
      await requestAccountDeletion();
    } catch {
      setDeleting(false);
      setConfirmingDelete(false);
      notify(
        'Request not sent',
        'Something went wrong and your account is unchanged. Please try again, or contact support if it keeps happening.',
      );
      return;
    }

    setDeleting(false);
    setConfirmingDelete(false);
    if (DELETION_RECEIPT_VISIBLE) {
      notify(
        'We have your request',
        /* No "nothing is erased" here either - see `DELETE_TERMS` on the
           privacy screen. Hidden behind the flag above today, and it must not
           come back saying something the rest of the app no longer says. */
        'Our team will look at it. Your account works as normal until then, so any bookings you have will go ahead. If you change your mind after it goes through, sign in with this same number and choose Restore.',
      );
    }
  }, []);

  /**
   * Trades the refresh token for a fresh pair, on the client's behalf.
   *
   * Registered below as `services/client`'s refresh handler - see the long
   * note there on what this replaces: a 401 used to go straight to
   * `onSignOut` because nothing ever called this. `refreshToken` is read
   * fresh on every call rather than closed over once, because the server
   * rotates it on every use - a callback built from the first one would work
   * exactly once and then start failing silently.
   */
  const tryRefresh = useCallback(async (): Promise<RefreshOutcome> => {
    if (!refreshToken) {
      return false;
    }
    try {
      const tokens = await refreshTokens(refreshToken);
      setAccessToken(tokens.accessToken);
      setRefreshToken(tokens.refreshToken);
      return true;
    } catch (error) {
      /*
       * Only the server refusing the token signs anybody out. Unreachable,
       * timed out and 5xx used to land here too and return `false`, which
       * `client.ts` reads as "session over" - so a customer lost their login
       * to a blink of bad signal and had to ask for an OTP again.
       */
      if (
        error instanceof ApiError &&
        (error.status === 401 || error.status === 403)
      ) {
        return false;
      }
      return 'unavailable';
    }
  }, [refreshToken]);

  /*
   * Hand the token to the API client, and tell it what to do about a 401 -
   * try `tryRefresh` first, and fall back to `onSignOut` only once that has
   * genuinely failed.
   *
   * The client is a plain module rather than a context - see the note in
   * `services/client.ts` - so it has no way to read React state. This effect
   * is the bridge, and it runs on every launch as well as on every change,
   * which is what makes a restored session actually authenticated rather than
   * merely looking it.
   *
   * Declared after `onSignOut` rather than beside the other persistence
   * effects, because it depends on it - hoisting would read the `const`
   * before it is assigned.
   */
  useEffect(() => {
    setClientToken(accessToken);
    setUnauthorizedHandler(onSignOut);
    setRefreshHandler(tryRefresh);

    /*
     * And, in the same breath, tell the server which handset to ring.
     *
     * It belongs here rather than in its own effect because it depends on the
     * line above it: `PUT /notifications/push-token` is an authenticated
     * request, so it can only be made after the token has actually reached
     * `client.ts`. Two effects would have raced, and the loser would have
     * registered nothing.
     *
     * Both directions matter. Registering ties this device to the account that
     * just signed in; forgetting on sign-out is what stops the once-per-token
     * guard inside `registerPushToken` from deciding the job is already done
     * when somebody else signs in on the same phone. Never awaited and never
     * surfaced - a handset with no Play Services still works, it simply falls
     * back to the in-app poll while the app is open.
     */
    if (accessToken) {
      registerPushToken(accessToken).catch(() => undefined);
    } else {
      forgetPushToken();
    }

    return () => {
      setUnauthorizedHandler(null);
      setRefreshHandler(null);
    };
  }, [accessToken, onSignOut, tryRefresh]);

  /** Chat opened from Help & Support, back to it - see `chatFromSupport`. */
  const backFromChat = useCallback(() => {
    setChatFromSupport(false);
    setTab('profile');
    setAccountPage('support');
  }, []);

  /*
   * The Android back button, walking the same stack the render branches do.
   *
   * Without this, every full screen in here is invisible to the system: this
   * app has no navigator, so a screen is a flag rather than an entry on a
   * stack, and Android had nothing to pop - back went straight past Home and
   * closed the app from wherever you were.
   *
   * Returning `false` on Home is deliberate: at the bottom of the stack there
   * is nothing left to unwind, and back should behave normally and leave.
   *
   * Sheets are not listed. `BottomSheet` registers its own handler while it is
   * open, and listeners run newest-first, so an open sheet always takes the
   * press before this does.
   */
  useEffect(() => {
    const unwind = () => {
      /*
       * Adding an address, which is drawn absolutely positioned over whatever
       * else is open rather than as a branch of the guard chain - so it is the
       * top of the stack visually and has to be the top of this list too.
       *
       * It was missing entirely, and was masked rather than handled:
       * `LocationScreen` registers its own back handler further down its own
       * flow, so the press was usually caught by something newer. "Usually" is
       * the problem - on the first stage of that screen there is nothing newer,
       * and back closed the booking or the tab underneath instead of the form
       * on top of it.
       */
      /*
       * The two full-page overlays that can sit over ANY branch - the review
       * composer and the notification feed. Both render above everything in
       * `sheets`, so they must be the first things back unwinds, ahead even
       * of the address form.
       */
      if (rating) {
        setRating(null);
        return true;
      }
      if (notificationsOpen) {
        setNotificationsOpen(false);
        return true;
      }
      if (addingAddress) {
        setAddingAddress(false);
        return true;
      }
      /*
       * Payment is above the cart, so it unwinds first.
       *
       * Back only drops the order - the cart stays open underneath, which is
       * where the customer came from and where the basket they are still
       * deciding about lives.
       *
       * This used to claim that `PaymentScreen`'s stages handled their own
       * back and that the gateway modal intercepted first. The second half was
       * true only of the gateway itself, which IS a `Modal` and does register
       * ahead of this; the first half was not true at all. `placing` and
       * `verifying` are plain screens, they registered nothing, and a back
       * press during either dropped the order to the cart while the booking
       * request was still in flight - creating a booking server-side that the
       * customer never saw confirmed. `PaymentScreen` now refuses the press
       * outright on those two stages; see the note there.
       */
      if (payOrder) {
        setPayOrder(null);
        return true;
      }
      // A service page can be opened from Home, search, a subcategory, the
      // cart or another service's "similar" rail - it has to win over all of
      // them, the same as its old sheet always rendered on top.
      if (openService) {
        setOpenService(null);
        return true;
      }
      if (searchOpen) {
        setSearchOpen(false);
        return true;
      }
      if (openSub) {
        setOpenSub(null);
        return true;
      }
      // The thread is a detour from the booking, so it unwinds first.
      if (openThread) {
        setOpenThread(null);
        return true;
      }
      /*
       * The pages opened from a booking, innermost first - the same order the
       * render branches use above. Back has to close the one actually on top,
       * and the two lists agreeing is the only thing that guarantees it.
       */
      if (walletOpen) {
        setWalletOpen(false);
        return true;
      }
      if (invoiceBooking) {
        setInvoiceBooking(null);
        return true;
      }
      /* Both halves of the SOS guard: the page is drawn for either, and an
         alarm raised from a ride used to ignore the key and let it change
         the tab hidden underneath instead. */
      if (sosFor || sosForRide) {
        setSosFor(null);
        setSosForRide(null);
        return true;
      }
      if (driverOpen) {
        setDriverOpen(false);
        return true;
      }
      if (newTicket) {
        setNewTicket(null);
        return true;
      }
      if (openTicket) {
        setOpenTicket(null);
        return true;
      }
      if (ticketsOpen) {
        setTicketsOpen(false);
        return true;
      }
      /*
       * No `rescheduling` branch, deliberately.
       *
       * The reschedule picker is a `SlotSheet`, and `BottomSheet` registers its
       * own back handler while it is open. Listeners run newest-first, so the
       * sheet always takes the press and closes itself before this handler is
       * consulted - exactly as the note at the top of this effect says sheets
       * do. The branch that used to sit here was unreachable code claiming to
       * do the job the sheet was already doing.
       */
      if (openBooking) {
        setOpenBooking(null);
        return true;
      }
      // Above the tab check: an account page sits on top of the account tab,
      // so backing out of it must land on the list, not on Home.
      if (accountPage) {
        /* Same as the header's arrow - see `PersonalInfoScreen.onBack`. The
           hardware button must abandon a parked checkout too, or backing out
           with it leaves an order that reopens later on its own. */
        pendingOrder.current = null;
        setAccountPage(null);
        return true;
      }
      /*
       * The cart returns to where it was opened from; every other tab returns
       * Home.
       *
       * The hardware button has to agree with the header's arrow - a Back that
       * lands somewhere different depending on whether it was pressed on screen
       * or on the device is the same bug twice, and harder to describe.
       */
      if (tab === 'cart') {
        setTab(cartOrigin);
        return true;
      }
      /* Bookings opened from the profile goes back to the profile - see
         `bookingsOrigin`. Reset on use so a later visit from the nav bar
         falls through to Home as it always did. */
      if (tab === 'bookings' && bookingsOrigin !== 'home') {
        setTab(bookingsOrigin);
        setBookingsOrigin('home');
        return true;
      }
      /* Chat opened from Help & Support goes back there - see
         `chatFromSupport`, and the same route as the arrow on the list. */
      if (tab === 'chat' && chatFromSupport) {
        backFromChat();
        return true;
      }
      if (tab !== 'home') {
        setTab('home');
        return true;
      }
      return false;
    };

    const sub = BackHandler.addEventListener('hardwareBackPress', unwind);
    return () => sub.remove();
  }, [
    accountPage,
    addingAddress,
    backFromChat,
    bookingsOrigin,
    cartOrigin,
    chatFromSupport,
    driverOpen,
    invoiceBooking,
    newTicket,
    notificationsOpen,
    openBooking,
    openService,
    openSub,
    openThread,
    openTicket,
    payOrder,
    rating,
    searchOpen,
    sosFor,
    sosForRide,
    ticketsOpen,
    tab,
    walletOpen,
  ]);

  /**
   * The only way a tab changes.
   *
   * Stable across renders so `BottomNav` is not handed a new function every
   * time anything on the page moves - and it clears the account's sub-page,
   * because leaving the account and coming back should land on the list.
   */
  const selectTab = useCallback(
    (next: TabKey) => {
      setAccountPage(null);
      setChatFromSupport(false);
      /* Opening the cart from the bottom nav records its origin too, so Back
         behaves the same however the basket was reached - see `cartOrigin`.
         Guarded against re-tapping Cart while already on it, which would
         otherwise make the origin 'cart' and strand Back. */
      setTab(current => {
        if (next === 'cart' && current !== 'cart') {
          setCartOrigin(current);
        }
        /* Picked from the nav bar, so Back means Home again - only the
           profile's own routes into the tab set a different origin. */
        if (next === 'bookings') {
          setBookingsOrigin('home');
        }
        return next;
      });

      /*
       * Re-read the bookings whenever that tab is opened.
       *
       * The app has no push, so nothing tells it that a professional has been
       * assigned, set off, or arrived. Opening the tab is the closest thing to
       * an intent to know, and it costs one small request. Pull-to-refresh on
       * the list itself covers the case where somebody is already looking at
       * it and waiting.
       */
      if (next === 'bookings') {
        orders.refresh().catch(() => {
          /* The hook records the failure and the list shows it. Nothing to
             add here. */
        });
      }
    },
    [orders],
  );

  /**
   * Items in the cart, counting only what the catalogue can still price.
   *
   * It used to sum the raw quantities, which counted keys the catalogue no
   * longer has - a service withdrawn since it was added, or an id from a
   * catalogue that has been replaced under a persisted cart. The bar then read
   * "1 service added · ₹0" and the cart it opened was empty, because both
   * the total below and the cart screen's own list resolve through the
   * catalogue and simply dropped it.
   *
   * Counting the same way they do means the bar can never advertise something
   * the cart will not show.
   */
  const cartCount = useMemo(
    () =>
      catalogue.services.reduce(
        (sum, service) => sum + (cart[service.key] ?? 0),
        0,
      ),
    [cart, catalogue.services],
  );

  /** Line total for the standing cart bar. Fees are added at checkout. */
  const cartTotal = useMemo(
    () =>
      catalogue.services.reduce(
        (sum, service) => sum + service.price * (cart[service.key] ?? 0),
        0,
      ),
    [cart, catalogue.services],
  );

  /**
   * A taste of the catalogue, not the whole thing.
   *
   * `popularFrom` decides the ORDER - the services this app was designed
   * around first, then a spread across the trades, then the rest - and the
   * limit caps it at a rail somebody can actually scan. "View all" beside the
   * heading is the way to the rest; a rail with all sixty-plus was a second
   * catalogue nobody asked to see before deciding to look.
   */
  /*
   * All of them, on request.
   *
   * ---------------------------------------------------------------------------
   * WHY THERE IS NO CAP ANY MORE
   * ---------------------------------------------------------------------------
   * This was 8, then 6 - numbers picked to keep the block short enough that
   * the trust strip below it stayed on the first screen. That reasoning holds
   * for a catalogue of sixty. It does not hold for this one: the platform
   * sells eleven services, and capping at six meant almost half of what is for
   * sale was reachable only through "View all" - a control most people never
   * press.
   *
   * `popularFrom` still decides the ORDER, which is the part that matters: the
   * spread runs one service per category before it repeats a category, so the
   * first rows are a range of trades rather than six variations of cleaning.
   * Handing it the whole length only removes the truncation at the end.
   *
   * A published `service_carousel` has never been capped - ops names the
   * services and gets exactly those. This makes the compiled path agree with
   * it rather than quietly showing less.
   */
  const popular = useMemo(
    () => popularFrom(catalogue.services, catalogue.services.length),
    [catalogue.services],
  );

  /**
   * The home screen as ops published it, or null.
   *
   * ---------------------------------------------------------------------------
   * THE BUILDER HAS BEEN LIVE A LONG TIME AND NOTHING ASKED FOR IT
   * ---------------------------------------------------------------------------
   * `ui_configs` on the server is versioned, draft-to-published-to-rollback,
   * per city, per customer segment, with a validator that checks every
   * category slug and service id exists. The admin console has an editor for
   * it. No app has ever called `GET /ui-config/home`, so anything composed
   * there was published into silence.
   *
   * `null` is the ordinary answer and means "draw the screen you already had".
   * Nothing published, a failed request, a tree this build is too old for, a
   * tree that will not parse - all of them land here, and everything below
   * falls back to the compiled content. See `usableTree`.
   */
  const [homeTree, setHomeTree] = useState<UiTree | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    /*
     * The city the address resolved to, when it has resolved.
     *
     * ---------------------------------------------------------------------------
     * THIS ASKED GLOBALLY, AND THAT MADE THE FEATURE LOOK BROKEN
     * ---------------------------------------------------------------------------
     * A tree is targeted per city, and this screen had nothing to name one
     * with, so it asked for the global publication only. The first thing
     * anybody published in the admin console was "Indore · all" - the console
     * asks for a city, so of course it was city-scoped - and it resolved to
     * nothing here. The console said "Published", the app drew its compiled
     * screen, and neither end reported anything wrong.
     *
     * `useServiceCity` already knows the answer: it reverse-geocodes the
     * address pin and matches it against `/cities` for the coverage banner.
     * Passing that id makes the server's own precedence work as designed -
     * city+segment, then city+all, then global - so a global publication still
     * reaches a customer whose city has none of its own.
     *
     * `undefined` until the geocode lands, which asks globally exactly as
     * before. That is a real first request, not a wasted one: for anyone in a
     * city with nothing published it is the whole answer, and the second
     * request only happens where a city id turns up.
     *
     * The SEGMENT it also varies by needs nothing from here: the route reads
     * it off the caller's own token.
     */
    fetchHomeConfig(serviceCity?.id, controller.signal)
      .then(setHomeTree)
      .catch(() => undefined);

    return () => controller.abort();
  }, [serviceCity?.id]);

  /**
   * Ask for the published layout again.
   *
   * The effect above runs on mount and when the city changes, and ops changes
   * a publication on neither of those. So a layout published while somebody
   * had the app open was invisible until the process was killed - the same
   * gap the catalogue had, on the one document whose whole purpose is being
   * changed from the console.
   *
   * The same read as the effect, not a second one: a pull that fetched
   * differently from the mount is two behaviours to keep in step, and only one
   * of them gets tested.
   *
   * Never rejects. A failed refresh leaves the tree exactly as it was, which
   * is right - the screen on display was published by somebody, and a lost
   * request is no reason to take it down.
   */
  const reloadHomeTree = useCallback(
    () =>
      fetchHomeConfig(serviceCity?.id)
        .then(setHomeTree)
        .catch(() => undefined),
    [serviceCity?.id],
  );

  /*
   * These two live here rather than at the top of the component because
   * `refreshPlatform` reads `reloadHomeTree`, which reads the resolved city.
   * Hoisting them above it compiles until it does not: a `const` read before
   * its declaration is a runtime error, not a warning.
   */
  /**
   * Everything a pull-to-refresh should re-read, wherever it is pulled.
   *
   * Two documents, and both go stale the same way - ops edits them while the
   * app is open and nothing asks again:
   *
   *  - the CATALOGUE: services, prices, categories and their pictures;
   *  - the ops SETTINGS: `booking.minNoticeHours`, `booking.slotStepMinutes`,
   *    the cancellation window and fee, the upload caps. `loadAppConfig` is
   *    called exactly once, at launch (see `App.tsx`), so before this the only
   *    way to pick up a changed notice period was to kill the app.
   *
   * The config never rejects - it falls back to its compiled defaults - but
   * the catch is here anyway so one slow document cannot leave a spinner
   * turning on the other's account.
   */
  const refreshPlatform = useCallback(
    () =>
      Promise.all([
        catalogue.reload(),
        loadAppConfig().catch(() => undefined),
        reloadHomeTree(),
        /* Ops can switch a service on for an area while the app is open, and
           a pull is how the customer asks again. */
        reloadAreaServices(),
      ]),
    [catalogue, reloadHomeTree, reloadAreaServices],
  );

  /*
   * The answer, published for every tile to read.
   *
   * Categories are worked out HERE because this is where the catalogue is: a
   * category is greyed only when every one of its services is unavailable, and
   * a tile has no way to know what is inside it. Rewritten whenever either
   * side changes - a new address, or a catalogue that reloaded with different
   * services in a category. The store ignores a result that did not move.
   */
  const unavailableHere = areaServices.services;
  useEffect(() => {
    setAreaAvailability({
      enforced: areaServices.enforced,
      services: unavailableHere,
      categories: unavailableCategories(
        catalogue.categories.map(category => category.key),
        key => catalogue.servicesIn(key).map(service => service.key),
        unavailableHere,
      ),
    });
  }, [areaServices.enforced, unavailableHere, catalogue]);

  /* Cleared on the way out, so nothing keeps greying services for an address
     nobody is looking at any more. */
  useEffect(() => () => setAreaAvailability(OPEN_AVAILABILITY), []);

  /**
   * One pull, both reads - bookings and the catalogue.
   *
   * `pulling` is its own flag rather than a reuse of `orders.loading`: the two
   * requests finish at different times, and driving the spinner off the
   * bookings alone stopped it while the catalogue was still in flight. The
   * customer let go, saw the spinner retract, and the prices changed a moment
   * later - which reads as the app correcting itself rather than as the
   * refresh they asked for.
   *
   * Neither can reject: `reload` never does, and a failed booking refresh is
   * already reported through `orders.error`.
   */
  const [pulling, setPulling] = useState(false);
  const pullToRefresh = useCallback(() => {
    setPulling(true);
    Promise.all([orders.refresh().catch(() => {}), refreshPlatform()]).finally(
      () => setPulling(false),
    );
  }, [orders, refreshPlatform]);

  /**
   * The banners out of the tree, for the hero.
   *
   * Lifted out rather than rendered in place: the hero IS the banner slot on
   * this screen - the nav, the address and the search field sit on the artwork
   * - so a published `banner_carousel` feeds it and the remaining blocks
   * render below it. Only the first is used; a second would have nowhere to go.
   *
   * `undefined` rather than `[]` when there is none, because that is what the
   * hero reads as "draw the compiled banners".
   */
  const publishedBanners = useMemo(() => {
    const carousel = homeTree?.components.find(
      component => component.type === 'banner_carousel',
    );
    return carousel?.type === 'banner_carousel' && carousel.items.length
      ? carousel.items
      : undefined;
  }, [homeTree]);

  /**
   * The tree, split by the section each block belongs to.
   *
   * ---------------------------------------------------------------------------
   * A PUBLICATION SUPPLEMENTS THIS SCREEN, IT DOES NOT REPLACE IT
   * ---------------------------------------------------------------------------
   * This used to be one `publishedBlocks`, rendered instead of every compiled
   * section below - so a tree was all or nothing. Publishing a single category
   * block, which reads as "I want to choose the categories", deleted Popular
   * Services from the page, because the tree happened not to mention it.
   *
   * Banners already worked the right way and this is that rule applied to the
   * rest: each section asks whether the tree has anything to say about ITS
   * block, and draws its own if not. Ops can change one part of the page
   * without knowing what else is on it.
   *
   * `undefined` rather than an empty tree throughout, because that is what the
   * sections below read as "draw the compiled one".
   */
  /**
   * Opens Refer & Earn from anywhere, including Home.
   *
   * ---------------------------------------------------------------------------
   * SETTING THE PAGE WAS NOT ENOUGH, AND NOTHING SAID SO
   * ---------------------------------------------------------------------------
   * The banner called `setAccountPage('refer')` alone. Every account page is
   * rendered inside `if (tab === 'profile')`, so from Home that set a value
   * nothing was reading: the tap registered, the state changed, and the screen
   * did not move. A dead control that looks alive.
   *
   * The tab has to go with it. Ungated on purpose - the offer is the reason to
   * sign in, so putting sign-in in front of it inverts the funnel; the referral
   * screen asks for an account only where it needs one, to key a code that can
   * be paid out.
   */
  const openReferral = useCallback(() => {
    setTab('profile');
    setAccountPage('refer');
  }, []);

  const publishedCategories = useMemo(
    () => sectionOf(homeTree, ['category_grid']),
    [homeTree],
  );
  const publishedServices = useMemo(
    () => sectionOf(homeTree, ['service_carousel']),
    [homeTree],
  );
  /*
   * Copy and spacing - whatever a tree carries that is not one of the three
   * sections this screen owns. Drawn after the rest, in the tree's own order,
   * because there is nothing compiled for them to sit beside.
   */
  const publishedExtras = useMemo(
    () => sectionOf(homeTree, ['text_block', 'spacer']),
    [homeTree],
  );

  /**
   * Half the page, less the gutters and the gap between the pair.
   *
   * Measured rather than a ratio of the window: the page is padded by
   * `screenPadding` on both sides and, on a tablet, narrowed again by
   * `resp.column`. A card sized off the raw width would overflow both.
   *
   * `Math.floor` because two halves of an odd width add up to more than the
   * row has, and the row would wrap the second card onto a line of its own.
   */
  /**
   * The referral card's height, from its width and `REFER_ASPECT`.
   *
   * A `minHeight` rather than an `aspectRatio`, and the difference is whether
   * the card survives a phone with large text. `aspectRatio` fixes the box and
   * clips whatever does not fit; a minimum holds the proportion at the normal
   * font scale and lets the card grow instead of losing a line. The same
   * lesson the address panel's own note carries.
   */
  const referHeight = Math.round(
    (resp.contentWidth - screenPadding * 2) / REFER_ASPECT,
  );

  const serviceTileWidth = Math.floor(
    (resp.contentWidth - screenPadding * 2 - spacing.sm) / 2,
  );

  /**
   * The biggest few categories, for the grid on the home page.
   *
   * Capped at `HOME_CATEGORY_COUNT` rather than showing everything. The full
   * list runs past twenty on a seeded catalogue, which is four rows of tiles
   * between the hero and every other section on the page - so the offer strip,
   * the popular rail and the rest start below the fold and are never seen. The
   * rest are one tap away behind "View all", which is exactly what that
   * control is for.
   *
   * Ordered by how much each has to sell, not by the server's own order:
   * `sortOrder` is 1 on nearly every category, so what came back was
   * effectively alphabetical and "AC Service" (1 service) led while Plumbing
   * (12) and Electrical (12) trailed. That order is what makes a cut of six
   * defensible - it keeps the six with the most behind them, rather than the
   * six that happen to sort first.
   *
   * Sorted on a copy: `catalogue.categories` is shared with the catalogue
   * sheet, which shows the platform's own order, and sorting in place would
   * silently reorder that too.
   */
  const homeCategories = useMemo(
    () =>
      [...catalogue.categories]
        .sort(
          (a, b) =>
            catalogue.servicesIn(b.key).length -
            catalogue.servicesIn(a.key).length,
        )
        .slice(0, HOME_CATEGORY_COUNT),
    [catalogue],
  );

  /**
   * A tap on a category tile.
   *
   * Offline the app knows its own subcategories, so it asks which one - the
   * step the sheet exists for. Online there is nothing finer than a category:
   * the platform stops there, so the sheet would be empty and the tap goes
   * straight to the category's services.
   */
  const openCategory = useCallback(
    (key: CategoryKey) => {
      const category = categoryFor(key);

      /* None of this trade reaches the address on screen. Read from the
         store at the moment of the tap, so it is never a render behind. */
      if (getAreaAvailability().categories.has(key)) {
        setComingSoonFor(category.label);
        return;
      }

      const services = catalogue.servicesIn(key);

      if (catalogue.source === 'server') {
        setOpenSub({
          key,
          label: category.label,
          blurb: `${services.length} service${
            services.length === 1 ? '' : 's'
          }`,
          category: key,
          serviceKeys: services.map(service => service.key),
        });
        return;
      }

      setPickingCategory(category);
    },
    [catalogue, categoryFor],
  );

  /** Unguarded - the gate is applied by the callers below. */
  const applyQuantity = useCallback((service: Service, delta: number) => {
    /*
     * An hourly service is one line however many times it is added.
     *
     * Adding the same one twice used to make a quantity of two: two separate
     * one-hour visits, two professionals, two slots to staff - when what the
     * customer meant was one visit of two hours. Nobody wants the same
     * service done twice in their house on the same day; they want it done
     * for longer. So a second add lengthens the booking instead of
     * duplicating it, which is also the only reading that matches the hour
     * chips sitting on the same row.
     *
     * The first add is deliberately NOT `min + delta`: it puts the service in
     * at its minimum, because "add" from a card means "I want this", not "I
     * want this for two hours".
     */
    if (isHourly(service) && delta > 0) {
      setCart(current =>
        current[service.key] ? current : { ...current, [service.key]: 1 },
      );
      setCartHours(current => ({
        ...current,
        [service.key]: hoursAfterAdd(service, current[service.key], delta),
      }));
      return;
    }

    setCart(current => {
      const next = { ...current };
      const have = next[service.key] ?? 0;
      /*
       * Adding stops at the service's per-booking cap (`maxPerBooking`, set
       * per service by ops). Each unit is its own visit and the checkout
       * refuses a basket past the cap. Every add in the app arrives here, so
       * the steppers only mirror this. A count already above the cap is never
       * raised, and never cut by an add.
       */
      const wanted =
        delta > 0
          ? Math.min(have + delta, Math.max(have, maxPerBooking(service)))
          : have + delta;
      if (delta > 0 && wanted === have) {
        return current;
      }

      if (wanted > 0) {
        next[service.key] = wanted;
      } else {
        delete next[service.key];
        /*
         * The length goes with the line.
         *
         * A stale entry is harmless while it sits there and wrong the moment
         * the same service is added again: it would come back at four hours
         * because of a choice made before it was removed, which reads as the
         * app deciding on its own however true the number is.
         */
        setCartHours(hours => forgetHours(hours, service.key));
      }
      return next;
    });
  }, []);

  /**
   * The picker's writer.
   *
   * Ignores a fixed service outright rather than trusting the caller: an entry
   * written against one would be sent to a platform that refuses hours on a
   * fixed service, and the refusal would land at checkout on a booking the
   * customer had already agreed to.
   */
  const changeHours = useCallback((service: Service, hours: number) => {
    if (!isHourly(service)) return;
    const { min, max } = hourRange(service);
    const clamped = Math.min(max, Math.max(min, Math.round(hours)));
    setCartHours(current => ({ ...current, [service.key]: clamped }));
  }, []);

  /*
   * Browsing is open; putting something in the cart is not.
   *
   * Opening a service stays ungated on purpose - asking who someone is before
   * showing them anything worth signing in for is the wrong order, and the
   * catalogue is the pitch. Adding is where a cart starts belonging to a
   * person, so that is where the sheet rises.
   *
   * Removing is never gated: nothing can be in the cart without having passed
   * the gate to put it there.
   */
  const changeQuantity = useCallback(
    (service: Service, delta: number) => {
      /* A changed basket is a fresh reason to show the bar - see
         `cartBarHidden`. Set before the change so the bar is already back when
         the new count renders. */
      setCartBarHidden(false);
      if (delta <= 0) {
        applyQuantity(service, delta);
        return;
      }
      /*
       * Adding only. Removing is always allowed, because the customer may have
       * put this in the basket at an address where it IS offered and then
       * changed address - they must be able to take it back out.
       *
       * Here, and not only in `openServiceSheet`, because a row's own "Add"
       * button comes straight to this without opening anything.
       */
      if (getAreaAvailability().services.has(service.key)) {
        setComingSoonFor(service.name);
        return;
      }
      gated(() => applyQuantity(service, delta));
    },
    [applyQuantity, gated],
  );

  /** Hides the standing cart bar. The basket is untouched - see `cartBarHidden`. */
  const hideCartBar = useCallback(() => setCartBarHidden(true), []);

  /**
   * What the cart bars are told the basket holds.
   *
   * Zero while dismissed, which is what makes every bar hide itself -
   * `CartBar` already renders nothing on an empty cart, so one derived number
   * turns all four call sites off without a guard at each of them.
   *
   * Deliberately NOT used for the tab badge: the basket is not empty, it is
   * only unannounced, and the Cart tab must keep showing what is in it.
   */
  const cartBarCount = cartBarHidden ? 0 : cartCount;

  /*
   * Every service tap in the app arrives here - the home rails, a category's
   * page, search, the services tab - so this is where "coming soon" is
   * answered instead of opening a service that cannot be booked at this
   * address.
   */
  const openServiceSheet = useCallback((service: Service) => {
    if (getAreaAvailability().services.has(service.key)) {
      setComingSoonFor(service.name);
      return;
    }
    setOpenService(service);
  }, []);

  /**
   * A service's category, as one stable function.
   *
   * Written out because `categoryFor={categoryOfService}`
   * inline at each call site makes a NEW function on every render of this
   * screen - and this screen re-renders on every cart change, every sheet
   * opening, every fetch. Each new identity changes a prop on every row and
   * card underneath it, so all the `React.memo` on `ServiceRow` and
   * `ServiceCard` bought was a wasted comparison before re-rendering anyway.
   *
   * The Services tab is where that showed: sixty rows, each with a photograph,
   * re-rendering on every keystroke in its search field.
   */
  const categoryOfService = useCallback(
    (service: Service) => categoryFor(service.category),
    [categoryFor],
  );

  /**
   * Book the same thing again.
   *
   * Puts the service in the basket and opens the cart, rather than opening the
   * service's own sheet as it used to. "Book again" is not "look at this
   * again" - somebody tapping it has already decided, and the sheet made them
   * decide a second time before anything went in the basket.
   *
   * Resolved by `serviceId` where the booking has one, which anything from the
   * platform does. The name match is a fallback and a poor one: the app's
   * catalogue and the platform's spell the same product differently
   * ("Bathroom deep cleaning" against "Bathroom Deep Clean"), so it found
   * nothing and the button did nothing at all.
   */
  const rebook = useCallback(
    (booking: Booking) => {
      const again =
        (booking.serviceId
          ? catalogue.services.find(item => item.key === booking.serviceId)
          : undefined) ??
        catalogue.services.find(item => item.name === booking.title);

      if (!again) {
        /* Withdrawn from the catalogue since it was booked. Saying so beats a
           button that quietly does nothing. */
        setCancelFailed(
          `${booking.title} is not available to book at the moment.`,
        );
        return;
      }

      /*
       * Asked before anything navigates. `changeQuantity` would refuse it too,
       * but this handler goes on to close the booking and open the cart - and
       * would land the customer on a basket without the thing they just asked
       * to book again.
       */
      if (getAreaAvailability().services.has(again.key)) {
        setComingSoonFor(again.name);
        return;
      }

      changeQuantity(again, 1);
      setOpenBooking(null);
      /* Same reason as `goToCart` below: an overlay left open would swallow
         the tab change and the customer would land nowhere. Rebooking is
         reachable from the booking detail screen, which is one. */
      setOpenService(null);
      setSearchOpen(false);
      setOpenSub(null);
      /* Rebooking always starts from a booking, so Back belongs on the
         bookings list - not on whatever tab happened to be behind it. */
      setCartOrigin('bookings');
      setTab('cart');
    },
    [catalogue.services, changeQuantity],
  );

  /**
   * Go to the cart from anywhere, including from on top of a full-screen view.
   *
   * ---------------------------------------------------------------------------
   * SETTING THE TAB IS NOT ENOUGH
   * ---------------------------------------------------------------------------
   * `openService`, `searchOpen` and `openSub` are checked BEFORE `tab` in the
   * render below, and each returns its own screen. So `setTab('cart')` on its
   * own, while one of them is open, sets state that nothing can show: the
   * overlay branch runs again and returns the same screen, and the press looks
   * dead.
   *
   * Worse, it looked dead and then acted later - the tab really had changed,
   * so pressing back to dismiss the overlay dropped the customer into the cart
   * they had apparently failed to open, seemingly at random.
   *
   * So every route to the cart closes the overlays first. One function, used
   * by every cart bar and every "View cart" button, rather than three call
   * sites that each have to remember which pieces of state to clear.
   */
  const goToCart = useCallback(() => {
    setOpenService(null);
    setSearchOpen(false);
    setOpenSub(null);
    /* Remembered before the tab moves - see `cartOrigin`. Guarded so that a
       second tap while already on the cart cannot overwrite the origin with
       'cart' and strand Back on the page it is trying to leave. */
    setTab(current => {
      if (current !== 'cart') {
        setCartOrigin(current);
      }
      return 'cart';
    });
  }, []);

  /** Opening the cart is free; it is checkout inside it that is gated. */
  const onProceedToCart = goToCart;

  /**
   * Booking blocked because we cannot reach the address, as a sheet.
   *
   * Held here rather than in the cart because the check that feeds it -
   * `outOfCoverage` - already lives on this screen, and because this is the
   * one place every route to payment passes through.
   */
  const [coverageBlocked, setCoverageBlocked] = useState(false);

  /**
   * The two gated steps: an account, and an address we can actually serve.
   *
   * ---------------------------------------------------------------------------
   * WHY THE STOP IS HERE AND NOT IN THE CART
   * ---------------------------------------------------------------------------
   * Coverage was warned about in two places and enforced in none. `CoverageBanner`
   * on Home and the line under the map both say "we are not in your area yet",
   * and both are deliberately passive - browsing and saving an address stay
   * open, which is right. But checkout stayed open too, so an address in a city
   * that is not on `/cities` minted an order, walked into payment, and failed
   * later as a server rejection with nothing a customer could act on.
   *
   * This is the choke point that closes it: `CartScreen`'s footer and every
   * other path to payment call this one callback, so gating here covers them
   * all rather than each screen growing its own copy of the rule.
   *
   * ---------------------------------------------------------------------------
   * WHY `=== false` AND NOT `!covered`
   * ---------------------------------------------------------------------------
   * `useCoverage` answers three ways and `outOfCoverage` already collapses that
   * to the only one worth blocking on - see its own note. `null` is "not known
   * yet", and treating an unlanded city list or a slow geocode as "not covered"
   * would stop a customer standing in Indore from booking. An unknown answer
   * lets the booking through: the server still checks, and a rare rejection
   * downstream beats routinely blocking people we do serve.
   *
   * Ordered after the auth gate on purpose. Signing in is the step that
   * produces the account this booking belongs to, and asking somebody to fix
   * their address first - then still facing a login - is two walls in a row.
   */
  const onCheckout = useCallback(
    (order: PaymentOrder) => {
      gated(() => {
        if (outOfCoverage) {
          setCoverageBlocked(true);
          return;
        }

        /*
         * A booking needs somebody's name on it.
         *
         * -------------------------------------------------------------------
         * WHY THE AUTH GATE DOES NOT COVER THIS
         * -------------------------------------------------------------------
         * `gated` asks one question - is there a session - and a phone-OTP
         * signup answers yes with `name: null`. So a brand-new customer went
         * straight from the OTP sheet to payment, and the professional was
         * dispatched to a job with no name attached: nothing in the flow ever
         * asked, and Personal Information was an account page they would have
         * had to go and find.
         *
         * Checked HERE rather than in `gated` deliberately. Browsing, adding
         * to the cart and opening a service need a session but not a name;
         * making somebody fill in a profile to look at prices is a wall in
         * front of the wrong door. Checkout is the first point where the name
         * is actually used, so it is where it is asked for.
         *
         * The order is parked exactly as `gated` parks its own action, so
         * saving the profile drops the customer back into payment instead of
         * on the account page, and they do not have to find their way back to
         * a basket they had already finished with.
         */
        if (!profileName?.trim()) {
          pendingOrder.current = order;
          setTab('profile');
          setAccountPage('edit-profile');
          return;
        }

        payFromCartRef.current(order);
      });
    },
    [gated, outOfCoverage, profileName],
  );

  /**
   * Paid, verified, booked.
   *
   * The basket is emptied here rather than in `PaymentScreen`: the cart is
   * this screen's state, and a payment screen reaching up to clear it would
   * make an abandoned payment and a completed one indistinguishable from the
   * outside.
   *
   * Both landings do the same three things - record, empty, close - and differ
   * only in where they leave you. That is the whole reason `land` is a
   * parameter instead of a second callback.
   */
  const onCreateBooking = useCallback(
    async (order: PaymentOrder) => {
      /*
       * The address the booking is actually for, recovered from the line the
       * header is showing. The cart and the payment order both carry the
       * address as formatted text - see `PaymentOrder.address` - but the
       * server needs the row, so the book is searched for the one that
       * formats to the same line, exactly as `addressNameFor` does.
       */
      const chosen = addressBook.addresses.find(
        saved => formatAddress(saved) === address,
      );
      if (!chosen) {
        throw new ApiError('Choose a delivery address before booking.', 0, []);
      }

      /*
       * `POST /bookings` takes the server's address id and refuses anything
       * else - it reads the city and the pin off that row to decide whether
       * the job can be dispatched at all. The app's address book is its own
       * local state, so the chosen address is put on file first.
       */
      const addressId = await ensureServerAddress(chosen);

      /*
       * The order, not the basket.
       *
       * `order.items` is the snapshot taken when "Proceed to Pay" was
       * tapped - one entry per unit, so a quantity of two books two visits -
       * and the basket may have changed since. Booking what was paid for
       * rather than what is currently in the cart is the whole reason that
       * snapshot exists.
       *
       * The mode follows what the server said it can take - see
       * `services/config`. With no gateway wired, `online` is refused with a
       * 501 and would park the booking in `awaiting_payment` with no way to
       * pay it, so `cash` is both the honest mode and the one that dispatches.
       *
       * `mode` is not sent: the server calls a booking scheduled when
       * `slotStartAt` is present and instant when it is absent.
       *
       * Deliberately not caught here. `PaymentScreen` has a `booking-failed`
       * stage for exactly this, and it reads differently from a failed
       * payment because the money has already been taken.
       *
       * ---------------------------------------------------------------------
       * ONE `POST /checkout`, NOT A LOOP OF `POST /bookings`
       * ---------------------------------------------------------------------
       * This used to call `orders.book`, which fans a basket out into one
       * `POST /bookings` per service - the only shape the old route understood.
       * Checkout prices and places the whole basket as one order, so it is
       * placed as one call. `order.code`, `order.tipAmount` and
       * `order.coinsToRedeem` are exactly what the cart's last quote showed -
       * re-deriving any of them here could place an order at a different price
       * than the one the customer agreed to.
       */
      const { bookings, order: placed } = await orders.placeCheckoutOrder(
        /* Always online and paid before anything is booked - never cash.
           See `basketPlacement`. */
        basketPlacement(order, addressId),
        /* Minted once per checkout in the cart and carried on the order, so
           tapping Retry re-sends the same key and returns the order already
           placed rather than making a second one. */
        order.reference,
      );
      return {
        bookings: bookings.map(booking => ({
          ...booking,
          checkoutId: placed.id,
        })),
        checkoutId: placed.id,
      };
    },
    [address, addressBook.addresses, orders],
  );

  /**
   * The repeating arrangement behind a "Recurring" checkout.
   *
   * One plan per service, like bookings - and no booking comes back, because
   * the server creates each visit when it falls due.
   */
  const onCreateRecurringPlan = useCallback(
    async (order: PaymentOrder) => {
      if (!order.recurrence) {
        throw new ApiError(
          'This plan has no schedule set. Reopen the cart and choose one.',
          0,
          [],
        );
      }

      const chosen = addressBook.addresses.find(
        saved => formatAddress(saved) === address,
      );
      if (!chosen) {
        throw new ApiError('Choose a delivery address before booking.', 0, []);
      }

      await createRecurringPlans({
        items: order.items,
        addressId: await ensureServerAddress(chosen),
        ...order.recurrence,
      });
    },
    [address, addressBook.addresses],
  );

  /**
   * Paid, verified, booked.
   *
   * The basket is emptied here rather than in `PaymentScreen`: the cart is
   * this screen's state, and a payment screen reaching up to clear it would
   * make an abandoned payment and a completed one indistinguishable from the
   * outside.
   *
   * Both landings do the same three things - close, empty, land - and differ
   * only in where they leave you. That is the whole reason `land` is a
   * parameter instead of a second callback.
   */
  /**
   * "Proceed to Pay", online: Razorpay opens over the cart, with no booking
   * screen in front of it.
   *
   * Nothing is booked before the money is in. The basket is placed as a
   * checkout that waits for payment, Razorpay opens at once, and the server
   * books every line only after it has confirmed the payment with Razorpay
   * itself. Closing the sheet, a declined card or a refused basket leaves the
   * customer on their cart with nothing booked - the one screen that follows
   * is the receipt, once they have paid.
   *
   * Never a cash booking: whatever `onlinePaymentsEnabled()` says - and it
   * says false before the config has loaded - the basket is paid first. Only
   * a recurring plan still opens `PaymentScreen`'s summary; it creates a plan,
   * not a booking.
   */
  const payFromCart = useCallback(
    (order: PaymentOrder) => {
      if (order.mode === 'recurring') {
        setPaidBasket(null);
        setPayOrder(order);
        return;
      }
      if (openingPaymentRef.current) return;
      openingPaymentRef.current = true;
      setOpeningPayment(true);

      payBasket(
        () => onCreateBooking(order),
        orders.loadBookings,
        order.items.length,
      )
        .then(run => {
          if (run.status === 'paid') {
            setPaidBasket({ bookings: run.bookings, at: new Date() });
            setPayOrder(order);
            return;
          }
          if (run.status === 'cancelled') {
            notify(
              'Payment cancelled',
              'Nothing has been booked and nothing was charged.',
            );
            return;
          }
          if (run.status === 'refused') {
            notify('Could not open payment', run.message);
            return;
          }
          notify(
            run.uncertain
              ? 'We could not confirm your payment'
              : 'Payment failed',
            run.uncertain
              ? 'If money left your account, your booking will appear in Bookings within a few minutes. Please check there before paying again.'
              : 'Nothing has been booked and nothing was charged. Please try again.',
          );
        })
        .catch(() => {
          notify(
            'Could not open payment',
            'Something went wrong before Razorpay opened. Nothing has been booked or charged.',
          );
        })
        .finally(() => {
          openingPaymentRef.current = false;
          setOpeningPayment(false);
        });
    },
    [onCreateBooking, orders.loadBookings],
  );
  payFromCartRef.current = payFromCart;

  const onPaid = useCallback((made: Booking[], land: 'booking' | 'home') => {
    setPayOrder(null);
    setCart({});
    // Emptied with it. These describe lines that no longer exist, and the
    // next basket must start from the service's own minimum rather than
    // from what the last one happened to be.
    setCartHours({});

    if (land === 'home') {
      setTab('home');
      return;
    }

    // The tab as well as the page: closing the detail has to land on the
    // bookings list, not back on Home.
    setTab('bookings');
    /* The server's booking, carrying the number the platform minted. There
         is nothing else to show - the app no longer invents one. */
    if (made[0]) {
      setOpenBooking(made[0]);
    }
  }, []);

  /**
   * Leaving payment without a booking - expired, refunded, or simply done
   * looking.
   *
   * The basket is deliberately kept. An expired slot is the one case where
   * someone is most likely to want the same services again immediately, and
   * emptying the cart underneath them turns "Book Again" into "start over".
   */
  const onLeavePayment = useCallback(() => {
    setPayOrder(null);
    setTab('home');
  }, []);

  /*
   * The bell's feed, from the platform's inbox.
   *
   * Loaded on sign-in and again whenever the list is opened, so a push that
   * arrived while the app was open is there when somebody looks. Emptied on
   * sign-out: the feed is whoever is signed in's, not the device's. A failed
   * load keeps what is on screen rather than blanking it.
   */
  const notificationsVisible =
    notificationsOpen || accountPage === 'notifications';
  useEffect(() => {
    if (!signedIn) {
      setNotifications([]);
      return undefined;
    }
    const controller = new AbortController();
    fetchNotifications(controller.signal)
      .then(setNotifications)
      .catch(() => undefined);
    return () => controller.abort();
  }, [signedIn, notificationsVisible]);

  const markRead = useCallback(
    (id: string) => {
      const item = notifications.find(entry => entry.id === id);
      setNotifications(current =>
        current.map(entry =>
          entry.id === id ? { ...entry, read: true } : entry,
        ),
      );
      if (item && !item.read) {
        markNotificationRead(id).catch(() => undefined);
      }
      /* A notification about a booking opens that booking. */
      const booking = item?.bookingId
        ? orders.bookings.find(entry => entry.id === item.bookingId)
        : undefined;
      if (booking) {
        setNotificationsOpen(false);
        setAccountPage(null);
        // Back from the detail lands on the bookings list, as after booking.
        setTab('bookings');
        setOpenBooking(booking);
      }
    },
    [notifications, orders.bookings],
  );

  const markAllRead = useCallback(() => {
    setNotifications(current => current.map(item => ({ ...item, read: true })));
    markAllNotificationsRead().catch(() => undefined);
  }, []);

  /**
   * Cancels a booking for real, wherever it actually lives.
   *
   * ---------------------------------------------------------------------------
   * WHY TWO PATHS
   * ---------------------------------------------------------------------------
   * The server owns the booking, so cancelling is one call and the row it
   * hands back replaces the local copy. Nothing is patched by hand any more.
   *
   * This used to end at a `ConfirmSheet` reading "Not connected yet" - nothing
   * before this ever mutated a booking, so admitting that was more honest than
   * pretending. Now that something does, the sheet below reports what actually
   * happened instead.
   */
  /**
   * Raises a result sheet only after the sheet that caused it has finished
   * closing.
   *
   * `CancelSheet`, `SlotSheet` and every `ConfirmSheet` are all `Modal`s, and
   * dismissing one in the same beat another is presented is exactly the
   * stacked-modal situation the notes all over this file warn about - on iOS
   * the two transitions collide and the app is left with a dead scrim and no
   * sheet, which is what "cancel/reschedule freezes the app" was. 400ms
   * clears `BottomSheet`'s 190ms close with margin to spare.
   */
  const afterSheetClose = useCallback((show: () => void) => {
    setTimeout(show, 400);
  }, []);

  /** True while a cancel or reschedule request is in flight - one at a time. */
  const bookingActionBusy = useRef(false);

  const cancelBooking = useCallback(
    async (booking: Booking, reason?: CancelReason) => {
      /*
       * The server decides, and it decides everything.
       *
       * The window, the fee, whether a refund is due and how much of one -
       * all of it comes back on the updated booking. The app used to work
       * those out itself from `windowFor`/`refundAmount` and patch its own
       * copy, which meant two systems computing money independently and
       * agreeing only by luck. They already disagreed: the app quoted a ₹49
       * cancellation fee against a platform setting of 0.
       */
      /*
       * The sheet closes BEFORE the request goes out, not after it lands.
       *
       * It used to stay up for the whole round trip with no spinner - a
       * confirm that visibly did nothing, tappable again and again, each tap
       * another request - and then the result sheet was presented in the same
       * frame this one was dismissed, which is the modal collision
       * `afterSheetClose` exists for. Closing first gives the tap an
       * immediate response and leaves only one modal in motion at a time.
       */
      if (bookingActionBusy.current) {
        return;
      }
      bookingActionBusy.current = true;
      setCancelling(null);

      try {
        await orders.cancel(booking.id, reason?.label);
        afterSheetClose(() => setCancelled({ booking, reason }));

        /* The detail screen underneath holds its own snapshot, so it needs
           the fresh row or it keeps showing a live booking. */
        const updated = orders.bookings.find(item => item.id === booking.id);
        setOpenBooking(current =>
          current && current.id === booking.id ? updated ?? current : current,
        );
      } catch (failure) {
        afterSheetClose(() =>
          setCancelFailed(
            failure instanceof Error
              ? failure.message
              : 'That could not be cancelled.',
          ),
        );
      } finally {
        bookingActionBusy.current = false;
      }
    },
    [afterSheetClose, orders],
  );

  /**
   * Move a booking to a slot the customer just picked.
   *
   * `orders.reschedule` moves it in one call and resolves to the SAME
   * booking, kept - the id and the reference do not change, so the detail
   * screen underneath is still pointing at a row that exists. It used to
   * cancel and recreate, which meant the id handed in belonged to a cancelled
   * booking by the time this returned; see `rescheduleBooking` for what that
   * cost besides the confusion.
   *
   * `describeSlot` is only used for the confirmation copy. The booking's own
   * `when` still comes from the server.
   */
  const rescheduleBookingTo = useCallback(
    async (booking: Booking, slot: { date: Date; time: string }) => {
      const startsAt = slotStartIso(slot.date, slot.time);

      /* Close-first, same as `cancelBooking` and for the same two reasons:
         the confirm responds at once instead of sitting dead for a round
         trip, and the result sheet never rises while this one's Modal is
         still mid-dismissal - the collision that froze the app. */
      if (bookingActionBusy.current) {
        return;
      }
      bookingActionBusy.current = true;
      setRescheduling(null);

      try {
        const created = await orders.reschedule(booking.id, startsAt);
        afterSheetClose(() =>
          setRescheduled({
            booking: created,
            /* `describeSlot` compares against midnight today so it can say
               "Today" and "Tomorrow" rather than a bare date. */
            when: describeSlot(slot.date, slot.time, startOfToday()),
          }),
        );

        setOpenBooking(current =>
          current && current.id === booking.id ? created : current,
        );
      } catch (failure) {
        afterSheetClose(() =>
          setRescheduleFailed(
            failure instanceof Error
              ? failure.message
              : 'That booking could not be moved.',
          ),
        );
      } finally {
        bookingActionBusy.current = false;
      }
    },
    [afterSheetClose, orders],
  );

  /**
   * Opening the picker, but only once the server has said a move is possible.
   *
   * ---------------------------------------------------------------------------
   * ASK BEFORE OFFERING, NOT AFTER CHOOSING
   * ---------------------------------------------------------------------------
   * `onReschedule` used to be `setRescheduling` itself, so every refusal the
   * server has - inside the reschedule cutoff, allowance spent, a booking too
   * far along to move - arrived only after somebody had picked a day, picked
   * a time and confirmed. The endpoint that answers this in advance exists
   * precisely so a picker never offers a slot the next call refuses.
   *
   * The sheet opens on the tap regardless, with Confirm held, so the button
   * responds at once rather than sitting dead for a round trip. If the answer
   * is no, the sheet closes and the server's own sentence is shown.
   */
  const openReschedule = useCallback(
    (booking: Booking) => {
      setAllowance(undefined);
      setRescheduling(booking);

      orders
        .rescheduleAllowance(booking.id)
        .then(answer => {
          if (answer.allowed) {
            setAllowance(answer);
            return;
          }

          /* Refused. Close the picker rather than leaving it open over a
             decision that cannot be made, and hand over the server's wording
             - it names the actual rule, which the app would only guess at.
             The refusal sheet waits for the picker's Modal to finish closing,
             or the two transitions collide - see `afterSheetClose`. */
          setRescheduling(null);
          afterSheetClose(() => setRescheduleFailed(answer.message));
        })
        .catch(() => {
          /* The check itself failed, which says nothing about whether a move
             is allowed. Release Confirm and let it ask the real question -
             the server refuses authoritatively either way, so the worst case
             is the behaviour this change replaced rather than a dead sheet. */
          setAllowance({
            allowed: true,
            refusal: null,
            message: '',
            hoursUntilSlot: null,
            reschedulesRemaining: 0,
            freeRescheduleHours: 0,
            currentSlotStartAt: null,
            earliestNewSlotAt: '',
          });
        });
    },
    [afterSheetClose, orders],
  );

  /*
   * The sheets every full-screen branch needs, built once.
   *
   * These used to be copied into each branch by hand, and the copies drifted:
   * the cart preview existed only on Home, so "View cart" on the standing bar
   * set its flag from a subcategory page and nothing appeared - the bar looked
   * broken everywhere except Home. Rendering one element in every branch means
   * a new branch cannot forget one, and a change here reaches all of them.
   *
   * Home-only sheets - the catalogue, notifications, the subcategory picker -
   * stay in the Home return, because nothing else can open them.
   */
  const sheets = (
    <>
      {/*
        Out of area, at the moment it actually stops something.

        In `sheets` rather than beside the cart so it survives the tab it was
        raised from - and so the one thing it offers, changing the address,
        opens the very sheet sitting next to it here.
      */}
      <CoverageSheet
        visible={coverageBlocked}
        address={address}
        onClose={() => setCoverageBlocked(false)}
        /* Close first: both are Modals, and raising the second over the first
           leaves two scrims stacked with the address list under both. */
        onChangeAddress={() => {
          setCoverageBlocked(false);
          setAddressOpen(true);
        }}
      />

      {/*
        A service or a trade not offered at this address yet.

        In `sheets` beside `CoverageSheet`, and for the same two reasons: it
        survives whichever full-screen view it was raised from, and the one
        thing it offers - another address - opens the sheet right below.
      */}
      <AreaComingSoonSheet
        visible={comingSoonFor !== null}
        subject={comingSoonFor}
        address={address === NO_ADDRESS ? '' : address}
        onClose={() => setComingSoonFor(null)}
        /* Close first: both are Modals, and raising the address sheet over
           this one would stack two scrims. */
        onChangeAddress={() => {
          setComingSoonFor(null);
          setAddressOpen(true);
        }}
      />

      {/*
        The same sheet the cart uses, and deliberately not a second one built
        to match: "where is the work" has one answer, and two sheets would be
        two places to fix the day the address book grows a field.
      */}
      <AddressSheet
        visible={addressOpen}
        addresses={addressBook.addresses}
        current={address}
        onClose={() => setAddressOpen(false)}
        onSelect={next => {
          /* The pin travels with the line, so coverage can be judged even
             when this row is not in the book the lookup searches - see
             `activeAddressCoords`. */
          onChangeAddress?.(formatAddress(next), next.coords);
          setAddressOpen(false);
        }}
        /*
          Close before leaving: the sheet is a Modal, and one left mounted over
          the location flow keeps its scrim on top.

          All three are `gated` for the reason the cart's own pair is - an
          address belongs to an account, so signed out this ran the entire flow
          and then threw the save away. Closing happens either way; only the
          journey is parked, and `gated` replays it once sign-in lands.
        */
        onAddNew={() =>
          leaveAddressSheetFor(() =>
            gated(() => {
              setAddressStage('fetching');
              setAddingAddress(true);
            }),
          )
        }
        onUseCurrentLocation={() =>
          leaveAddressSheetFor(() =>
            gated(() => {
              setAddressStage('fetching');
              setAddingAddress(true);
            }),
          )
        }
        onClosed={runAfterAddressSheet}
        /* The same destination as adding, for now - the location flow cannot
           load an existing address, so editing one opens an empty form. */
        onEdit={() =>
          leaveAddressSheetFor(() => gated(() => setAddingAddress(true)))
        }
      />

      {/*
        Adding an address, over whatever is already open - see `addingAddress`
        above for why this lives here and not as its own screen branch.

        Absolutely positioned rather than a sibling in a column: two children
        both claiming `flex: 1` would split the screen between them.
      */}
      {addingAddress ? (
        <View style={styles.addingAddress}>
          <SlideIn>
            <LocationScreen
              onDone={async saved => {
                onChangeAddress?.(formatAddress(saved), saved.coords);

                /*
                 * The contact name rides in the landmark line on the wire.
                 *
                 * `customer_addresses` has no column for a name, and the
                 * landmark is the free-text the professional is already shown
                 * to find the door - which is exactly what the name is for.
                 * See `SavedAddress.contactName`.
                 */
                const landmark =
                  [
                    saved.contactName ? `Ask for ${saved.contactName}` : null,
                    saved.note,
                  ]
                    .filter(Boolean)
                    .join(' · ') || undefined;
                /*
                 * Best-effort. `ensureServerAddress` already re-attempts this
                 * from the pin at checkout, so a failure here - signed out,
                 * offline, a dropped request - costs nothing but an
                 * immediate appearance in the list; it does not cost the
                 * ability to book against it.
                 */
                if (saved.coords) {
                  try {
                    /* An edit writes back to the row it opened; an add makes a
                       new one. Same screen, same payload - only the verb and
                       the id differ. */
                    if (editingAddress) {
                      await addressBook.update({
                        id: editingAddress.place.id,
                        label: saved.label,
                        addressLine: formatAddress(saved),
                        landmark,
                        lat: saved.coords.lat,
                        lng: saved.coords.lng,
                      });
                    } else {
                      const stored = await addressBook.save({
                        label: saved.label,
                        addressLine: formatAddress(saved),
                        landmark,
                        lat: saved.coords.lat,
                        lng: saved.coords.lng,
                      });

                      /*
                       * Saved, but somewhere we cannot reach yet - say so now
                       * rather than at checkout.
                       *
                       * `save` keeps an out-of-area address on the device and
                       * returns it flagged, so this is the one moment the app
                       * knows both that the address is on file AND that we do
                       * not serve it. Silence here would let someone add an
                       * address, see it appear in the list like any other,
                       * and only learn the truth after choosing services.
                       *
                       * Worded as a confirmation and not a warning: the thing
                       * they asked for happened.
                       */
                      if (stored.serviceable === false) {
                        notify(
                          "Saved - we're coming to your city soon",
                          `${NOT_SERVICEABLE_MESSAGE} We've saved this address and we'll let you know the moment we start serving it.`,
                        );
                      }
                    }
                  } catch (failure) {
                    /*
                     * Best-effort, but NOT silent.
                     *
                     * This used to be a bare `catch {}`, and that is how a
                     * wrong endpoint went unnoticed: every save threw, the
                     * throw was swallowed, and the list simply stayed empty
                     * with nothing reported anywhere. The booking path still
                     * recovers via `ensureServerAddress`, so this is not fatal
                     * - but someone who pressed Save and sees no address needs
                     * telling, and a developer needs the reason.
                     */
                    console.warn('Saving the address failed', failure);

                    /*
                     * Out of area is not an error, and must not be dressed as
                     * one.
                     *
                     * The server refuses the save with "This pin is outside
                     * the cities currently served by the platform", and that
                     * sentence went straight into an alert titled "Address not
                     * saved" - which reads as a fault, gives a customer
                     * nothing to do about it, and is the platform's internal
                     * vocabulary rather than an answer. Nothing has gone
                     * wrong: we simply are not there yet, and saying so with a
                     * "soon" is the honest and kinder version of the same
                     * fact.
                     */
                    /*
                     * The out-of-area case no longer reaches here.
                     *
                     * `useAddresses.save` now keeps a refused address on the
                     * device and RETURNS it rather than throwing - see its
                     * own note - so this catch only ever sees a real failure:
                     * offline, a 401, a malformed line. The branch that used
                     * to say "we're coming to your city soon" from here has
                     * moved to `onDone` below, where it can be said about an
                     * address that was actually kept.
                     *
                     * The check stays as a guard: if the server ever starts
                     * refusing for a reason `isNotServiceableError` matches
                     * but `save` did not catch, this must not call it a fault.
                     */
                    const outOfArea = isNotServiceableError(failure);
                    /*
                     * Signed out is not a fault, and must not be reported in
                     * the server's words.
                     *
                     * An address is saved against an account, so saving one
                     * without a session is a 401 - and the API hands back the
                     * guard's own sentence, "Missing bearer token", which went
                     * straight into the dialog. That names a mechanism the
                     * customer has never heard of, for a situation they can
                     * fix in two taps, and reads as the app being broken.
                     *
                     * `isUnauthorized` rather than matching the text: the
                     * status is the contract and the wording is not, so a
                     * reworded guard on the server must not put that sentence
                     * back in front of anybody.
                     */
                    const signedOut =
                      failure instanceof ApiError && failure.isUnauthorized;

                    notify(
                      outOfArea
                        ? "We're coming to your city soon"
                        : signedOut
                        ? 'Sign in to save this address'
                        : 'Address not saved',
                      outOfArea
                        ? `${NOT_SERVICEABLE_MESSAGE} We'll let you know as soon as we do.`
                        : signedOut
                        ? 'Your addresses are kept on your account. Sign in and we will save this one for you.'
                        : failure instanceof Error
                        ? failure.message
                        : 'We could not save that address. You can still book with it.',
                    );
                  }
                }
                setAddingAddress(false);
                setEditingAddress(null);
              }}
              onSkip={() => {
                setAddingAddress(false);
                setEditingAddress(null);
              }}
              /*
               * Whatever opened the flow decides where it starts - see
               * `addressStage`. Adding an address lands on the map; the
               * sheet's "Use current location" lands on the same map by way
               * of a GPS fix.
               *
               * This was the literal `"search"`, on the reasoning that
               * somebody adding an address is usually not standing at it. That
               * is still true, and the map is still the better first screen:
               * it shows WHERE the pin is going, and its search field opens
               * the same typing flow one tap away - whereas a text box shows
               * nothing until you have already typed something.
               *
               * Only for a plain add. An edit opens on its own pin instead -
               * `LocationScreen` ignores this when `initialAddress` is set,
               * because sending someone who pressed "Edit" to a blank search
               * box is what made the panel come back empty.
               */
              initialStage={addressStage}
              initialAddress={editingAddress ?? undefined}
            />
          </SlideIn>
        </View>
      ) : null}

      {/*
        Notifications, as a full page over whatever raised it.

        This used to be a slide-up sheet. It is an overlay - the same trick as
        the address form above - rather than a branch of the guard chain, so
        whatever was open underneath (Home, Chat, a booking) never unmounts:
        Back closes this and lands EXACTLY where the bell was tapped, scroll
        position and all, instead of on whichever branch the chain picked.
      */}
      {notificationsOpen ? (
        <View style={styles.addingAddress}>
          <SlideIn>
            <NotificationsScreen
              notifications={notifications}
              onBack={() => setNotificationsOpen(false)}
              onRead={markRead}
              onReadAll={markAllRead}
            />
          </SlideIn>
        </View>
      ) : null}

      <AuthSheet
        visible={authOpen}
        onClose={() => {
          // Abandoning sign-in must drop the parked action, or it would fire
          // the next time the user signs in for something else entirely.
          pending.current = null;
          setAuthOpen(false);
        }}
        onVerified={onVerified}
      />

      {/*
        Rating a finished job - a full page now, not a sheet.

        The sheet showed its opener through the scrim, and on the edit path
        that read as "it took me back to the profile with a modal on top".
        An overlay page covers the screen entirely and, like notifications
        above, leaves the opener mounted - Back from here lands on the booking
        detail or the ratings list it was opened from, unchanged.
      */}
      {rating ? (
        <View style={styles.addingAddress}>
          <SlideIn>
            <ReviewScreen
              booking={rating}
              existing={reviews[rating.id]}
              onBack={() => setRating(null)}
              onSubmit={review => {
                saveReview(review);
                setRating(null);
              }}
            />
          </SlideIn>
        </View>
      ) : null}

      {/*
        Log out, said before it happens rather than after.

        The row used to sign out on the bare tap, and the only visible change
        was the row vanishing - which read as "nothing happened". The sheet
        states the consequence (browsing continues as a guest) and gives the
        way out; confirming lands on Home as a guest, which is the state the
        app opens in before any sign-in.
      */}
      <ConfirmSheet
        visible={confirmingLogout}
        icon="logout"
        tone="danger"
        title="Log out?"
        body="You'll continue as a guest - you can browse services, and your cart stays on this phone. Sign in again any time with your number."
        confirmLabel="Log out"
        cancelLabel="Stay signed in"
        onConfirm={() => {
          setConfirmingLogout(false);
          onSignOut();
          /* As a guest the account list is mostly locked rows; Home is where
             a guest can actually do something, so that is where this lands. */
          setAccountPage(null);
          setTab('home');
        }}
        onClose={() => setConfirmingLogout(false)}
      />

      {/*
        The check that stops a mis-tap. Nothing more.

        Apple requires deletion to be reachable from inside the app (5.1.1(v));
        it also allows a confirmation step so nobody does this by accident,
        which is all this is.

        Deliberately bare. The body used to carry a paragraph - that the
        account keeps working until our team acts, that nothing is erased, that
        it comes back from the same number - and every word of that is still
        said, in the message that appears the moment this is confirmed and
        again on the deleted screen. Twice on the way to one tap is a wall of
        text nobody reads, and this sheet has one question to ask.

        `cancelLabel` leads, and the heavier action is the one that has to be
        reached for deliberately.
      */}
      <ConfirmSheet
        visible={confirmingDelete}
        icon="trash"
        tone="danger"
        title={deleting ? 'Deleting…' : 'Delete your account?'}
        body="Are you sure?"
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        cancelLabel="Keep my account"
        onConfirm={() => {
          // The request is in flight; a second tap must not start another.
          if (deleting) return;
          void runDeleteAccount();
        }}
        onClose={() => {
          if (deleting) return;
          setConfirmingDelete(false);
        }}
      />

      {/*
        What cancelling costs, decided by the stage - see the sheet.

        `CancelSheet`'s own button ("Cancel booking" / "Request to stop work")
        already IS the confirmation - the money panel, the reason, the fee are
        all stated before it. `onConfirm` used to only close this sheet and
        open a second one that did nothing; now it is the one place that
        actually cancels, the same as `RatingsScreen`'s `ConfirmSheet` acting
        the moment its own confirm button is pressed.
      */}
      <CancelSheet
        booking={cancelling}
        onClose={() => setCancelling(null)}
        onConfirm={cancelBooking}
      />

      {/*
        Moving a booking, using the very same picker the cart books with.

        Deliberately not a second date-and-time UI. A reschedule that offered
        different times from the ones on sale at checkout would be the app
        contradicting itself about when it works, and two grids would drift
        the first time the schedule changed. `visible` is driven by
        `rescheduling` so the sheet is mounted only while a booking is
        actually being moved.
      */}
      <SlotSheet
        visible={rescheduling !== null}
        /* Starts blank rather than on the current slot: the whole reason this
           sheet is open is that the existing time does not work. */
        slot={null}
        onClose={() => setRescheduling(null)}
        onConfirm={(slot: Slot) =>
          rescheduling ? rescheduleBookingTo(rescheduling, slot) : undefined
        }
        /*
          The server's own floor, so the grid never offers a time the next
          call would refuse. Absent while the check is in flight or when it
          came back empty, in which case the sheet falls back to the ordinary
          booking notice - Confirm is held anyway while `busy`.
        */
        notBefore={
          allowance?.earliestNewSlotAt
            ? new Date(allowance.earliestNewSlotAt)
            : undefined
        }
        note={allowance?.message || undefined}
        busy={allowance === undefined}
        /* The booking's own address, so a move is offered only onto slots that
           can actually be staffed for where the visit is. Absent on the
           bundled examples, which carry no server row - see `Booking.addressId`
           - and the grid then stays open, exactly as before capacity existed. */
        addressId={rescheduling?.addressId}
        serviceId={rescheduling?.serviceId}
        /* Its own Pro and slot are what the move gives up, so they must not
           make the new times look full. Also carries the hours it was sold. */
        bookingId={rescheduling?.serviceId ? rescheduling.id : undefined}
      />

      <ConfirmSheet
        visible={rescheduled !== null}
        icon="calendar"
        tone="neutral"
        title="Booking moved"
        body={
          rescheduled
            ? `${rescheduled.booking.reference} is now ${rescheduled.when}. We have told your professional.`
            : ''
        }
        confirmLabel="Done"
        /* Reports a result; there is nothing to decline. */
        cancelLabel={null}
        onConfirm={() => setRescheduled(null)}
        onClose={() => setRescheduled(null)}
      />

      <ConfirmSheet
        visible={rescheduleFailed !== null}
        icon="alert"
        tone="danger"
        title="Could not move that booking"
        body={rescheduleFailed ?? ''}
        confirmLabel="Close"
        /* Reports a failure; "Back" dismissed identically. */
        cancelLabel={null}
        onConfirm={() => setRescheduleFailed(null)}
        onClose={() => setRescheduleFailed(null)}
      />

      {/*
        The result, as its own confirmation rather than a toast.

        Genuinely tells you what happened now that `cancelBooking` did
        something - this used to read "Not connected yet" because nothing
        before it had ever actually cancelled anything, and pretending
        otherwise would have been the bigger lie.
      */}
      <ConfirmSheet
        visible={cancelled !== null}
        icon="checkCircle"
        tone="neutral"
        title="Booking cancelled"
        body={
          cancelled
            ? `${cancelled.booking.reference} is cancelled. Any refund goes back the way you paid, and we will tell you when it lands.`
            : ''
        }
        confirmLabel="Done"
        /* Reports a result; there is nothing to decline. */
        cancelLabel={null}
        onConfirm={() => setCancelled(null)}
        onClose={() => setCancelled(null)}
      />

      {/*
        And when it did not go through.

        A cancellation that fails silently is the worst of both: the customer
        believes the visit is off and somebody still turns up. The server's own
        message is shown rather than a generic one - "work has already started"
        needs a different response from "no connection", and only the server
        knows which it was.
      */}
      <ConfirmSheet
        visible={cancelFailed !== null}
        icon="alert"
        tone="neutral"
        title="Not cancelled"
        body={cancelFailed ?? ''}
        confirmLabel="I understand"
        /* Reports a failure; "Close" dismissed identically. */
        cancelLabel={null}
        onConfirm={() => setCancelFailed(null)}
        onClose={() => setCancelFailed(null)}
      />
    </>
  );

  /*
   * Only Home and Account exist so far. Bookings and Support keep showing
   * Home rather than a blank screen - a stub that says nothing is worse than
   * the tab appearing not to respond.
   */
  /*
   * A closed account, above every other branch.
   *
   * It has to be first. The session is real - signing in on a deleted account
   * succeeds, because the number was never retired - but the server refuses
   * every route except reading the account, restoring it and signing out. Any
   * screen below this would mount, fire its own reads, and fail one panel at a
   * time with nothing anywhere saying why.
   *
   * Rendering it here also means the tabs, the cart and the sheets are not
   * mounted at all, so there is nothing behind it to reach with a back gesture.
   */
  if (accountDeletedAt) {
    return (
      <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
        <AccountDeletedScreen
          /*
           * The account is open again, so this component simply stops being
           * rendered and the app is back on the home tab - with the same
           * session, which is still valid because it was never revoked a
           * second time.
           */
          onRestored={() => setAccountDeletedAt(null)}
          onSignOut={onSignOut}
        />
      </SafeAreaView>
    );
  }

  /*
   * Payment sits on top of every tab, cart included - a checkout in progress
   * has to win over a stray tap on the bar, or the order it is holding is
   * lost from under the customer. `unwind` below gives back only the order;
   * whichever tab was underneath (Cart, normally) is still there once it does.
   */
  if (payOrder) {
    return (
      <SlideIn>
        {/* No top edge: `PageHeader` and `PaymentResult` each pad for the
            status bar themselves, and a top edge here as well doubled it -
            the blank band above "Booking Details". */}
        <SafeAreaView style={styles.root} edges={['left', 'right']}>
          <PaymentScreen
            order={payOrder}
            paid={paidBasket ?? undefined}
            onBack={() => setPayOrder(null)}
            onPaid={onPaid}
            onCreateBooking={onCreateBooking}
            onLoadBookings={orders.loadBookings}
            onCreatePlan={onCreateRecurringPlan}
            onHome={onLeavePayment}
            // Support already has a home. "Need Help?" on a failed payment
            // goes to it rather than to a second, payment-only help page.
            onHelp={() => {
              setPayOrder(null);
              setTab('profile');
              setAccountPage('support');
            }}
          />

          {sheets}
        </SafeAreaView>
      </SlideIn>
    );
  }

  if (openService) {
    return (
      <SlideIn>
        <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
          <ServiceScreen
            service={openService}
            categoryFor={categoryOfService}
            quantity={cart[openService.key] ?? 0}
            /* The whole basket, so the footer can offer a way to it even when
               THIS service is not the thing already in it. */
            cartCount={cartCount}
            hours={cartHours[openService.key]}
            onChangeHours={changeHours}
            onChangeQuantity={changeQuantity}
            onBack={() => setOpenService(null)}
            onProceed={onProceedToCart}
            onOpenService={setOpenService}
          />
          {sheets}
        </SafeAreaView>
      </SlideIn>
    );
  }

  if (searchOpen) {
    return (
      <SlideIn>
        <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
          <SearchScreen
            services={catalogue.services}
            categories={catalogue.categories}
            categoryFor={categoryFor}
            servicesIn={catalogue.servicesIn}
            onBack={() => setSearchOpen(false)}
            onOpenService={openServiceSheet}
          />

          {/* The same bar the subcategory screen carries, and for the same
            reason: adding from a search result used to go quiet, leaving the
            cart reachable only by backing out to Home. No tab bar under it
            here, so it clears the nav buttons itself. */}
          <CartBar
            insetBottom
            count={cartBarCount}
            total={cartTotal}
            onDismiss={hideCartBar}
            /* Closes this overlay on the way - `setTab` alone cannot show the
               cart from here. See `goToCart`. */
            onPress={goToCart}
          />

          {sheets}
        </SafeAreaView>
      </SlideIn>
    );
  }

  if (openSub) {
    return (
      <SlideIn>
        <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
          <SubCategoryScreen
            sub={openSub}
            /* Online the shelf is the category itself, so the list comes from
               the catalogue rather than from the seed's groupings. */
            services={
              catalogue.source === 'server'
                ? catalogue.servicesIn(openSub.category)
                : undefined
            }
            category={categoryFor(openSub.category)}
            cart={cart}
            onChangeQuantity={changeQuantity}
            onBack={() => setOpenSub(null)}
            onOpenService={openServiceSheet}
          />

          {/* The same cart bar as Home, so adding here still shows a running
            total instead of going quiet until you navigate back. There is no
            tab bar under it here, so it has to clear the nav buttons itself. */}
          <CartBar
            insetBottom
            count={cartBarCount}
            total={cartTotal}
            onDismiss={hideCartBar}
            /* Closes this overlay on the way - `setTab` alone cannot show the
               cart from here. See `goToCart`. */
            onPress={goToCart}
          />

          {sheets}
        </SafeAreaView>
      </SlideIn>
    );
  }

  /*
   * Above the booking, not instead of it: `openBooking` stays set underneath,
   * so closing the thread reveals the page it was opened from.
   */
  if (openThread) {
    return (
      <SlideIn>
        <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
          <ChatScreen
            categoryFor={categoryFor}
            bookings={orders.bookings}
            initialBooking={openThread}
            onExit={() => setOpenThread(null)}
          />
          {sheets}
        </SafeAreaView>
      </SlideIn>
    );
  }

  /*
   * ---------------------------------------------------------------------
   * THE PAGES THAT SIT OVER A BOOKING
   * ---------------------------------------------------------------------
   * Ordered innermost first, like every other branch on this screen: the
   * invoice, the safety screen and the support pages are all opened *from* a
   * booking, so they have to win over it - and closing one lands back on the
   * booking rather than out on the list.
   *
   * `unwind` walks the same order for the hardware back button. The two must
   * agree, or back closes a screen that is not the one on top.
   */
  if (walletOpen) {
    return (
      <SlideIn>
        {/* No `top` edge: the banner at the head of the page bleeds under the
            status bar, the same way Home's own hero does. `WalletScreen`
            insets its own back button off `useSafeAreaInsets` instead. */}
        <SafeAreaView style={styles.root} edges={['left', 'right']}>
          <WalletScreen
            onBack={() => setWalletOpen(false)}
            bookings={orders.bookings}
            onOpenBooking={booking => {
              setWalletOpen(false);
              setOpenBooking(booking);
            }}
            /* The balance can only be spent on a booking, so "Redeem now"
               closes the wallet and lands on the catalogue. The cart applies
               it at checkout - see `CartScreen`. */
            onRedeem={() => {
              setWalletOpen(false);
              setTab('services');
            }}
          />
          {sheets}
        </SafeAreaView>
      </SlideIn>
    );
  }

  if (invoiceBooking) {
    return (
      <SlideIn>
        <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
          <InvoiceScreen
            booking={invoiceBooking}
            /* Whoever is actually signed in - a name if one was given, the
               verified number otherwise. Never a placeholder: this used to
               print the same invented name on every account's invoice. */
            billedTo={
              profileName || (phone ? formatPhone(phone) : 'Homingo customer')
            }
            onBack={() => setInvoiceBooking(null)}
          />
          {sheets}
        </SafeAreaView>
      </SlideIn>
    );
  }

  if (sosFor || sosForRide) {
    return (
      <SlideIn>
        <SafeAreaView style={styles.root} edges={['left', 'right']}>
          <SosScreen
            booking={sosFor ?? undefined}
            ride={sosForRide ?? undefined}
            onBack={() => {
              setSosFor(null);
              setSosForRide(null);
            }}
          />
          {sheets}
        </SafeAreaView>
      </SlideIn>
    );
  }

  if (driverOpen) {
    return (
      <SlideIn>
        <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
          <DriverScreen
            onBack={() => setDriverOpen(false)}
            /* Ordered. Leave the page and land on Bookings, which is where a
               live ride is actually shown - `DriverCard` there polls for the
               driver being assigned. Staying here would leave the customer
               looking at the sales page for a trip they have already booked. */
            onBooked={() => {
              setDriverOpen(false);
              setTab('bookings');
            }}
          />
          {sheets}
        </SafeAreaView>
      </SlideIn>
    );
  }

  if (newTicket) {
    return (
      <SlideIn>
        <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
          <NewTicketScreen
            booking={newTicket === true ? undefined : newTicket}
            onBack={() => setNewTicket(null)}
            onSubmit={ticket => {
              setTickets(current => [ticket, ...current]);
              setNewTicket(null);
              // Straight into the thread it created, which is where the reply
              // will land - a list with a new row on it answers nothing.
              setOpenTicket(ticket);
            }}
          />
          {sheets}
        </SafeAreaView>
      </SlideIn>
    );
  }

  if (openTicket) {
    return (
      <SlideIn>
        <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
          <TicketThreadScreen
            ticket={openTicket}
            onBack={() => setOpenTicket(null)}
          />
          {sheets}
        </SafeAreaView>
      </SlideIn>
    );
  }

  if (ticketsOpen) {
    return (
      <SlideIn>
        <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
          <TicketsScreen
            tickets={tickets}
            onBack={() => setTicketsOpen(false)}
            onOpen={setOpenTicket}
            onNew={() => setNewTicket(true)}
          />
          {sheets}
        </SafeAreaView>
      </SlideIn>
    );
  }

  if (openBooking) {
    return (
      <SlideIn>
        <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
          <BookingDetailScreen
            booking={openBooking}
            onBack={() => setOpenBooking(null)}
            /*
             * The professional's own thread, not the Chat tab.
             *
             * `openBooking` is left set either way, so closing the thread
             * (see the `openThread` branch above) reveals the booking again
             * instead of landing on the chat list with no way back. `Thread`
             * itself reads messages straight from the booking's own id - see
             * `services/chat.ts` - so there is nothing to look up here.
             */
            onMessage={booking => {
              if (booking.professional) {
                setOpenThread(booking);
              }
            }}
            /*
             * Calling the professional, through the same gate as everything
             * else that needs an account.
             *
             * `gated` parks the press and raises the auth sheet when nobody is
             * signed in, so a tap on Call from a signed-out session ends in a
             * call rather than in a 401 - the same path "Book Now" takes.
             *
             * `signedIn` has to go down with it, and that is not decoration.
             * The detail screen asks the platform whether this call is allowed,
             * and that question cannot be asked without a session - so signed
             * out the answer was "no", the button was disabled, its `onPress`
             * never fired, and this wrapper could never run. The gate was
             * unreachable behind the very state it existed for. Told who is
             * signed in, the screen keeps the button live and lets the press
             * land here.
             *
             * The call itself is drawn nowhere near here. `VoiceCallProvider`
             * owns it and its overlays are `Modal`s mounted in `App.tsx`, so
             * this screen's own guard chain and its back-button `unwind` are
             * untouched by a call arriving over the top of them.
             */
            signedIn={signedIn}
            onCall={(booking, counterpartName) =>
              gated(() =>
                voice?.startCall('booking', booking.id, counterpartName),
              )
            }
            review={reviews[openBooking.id]}
            onRate={setRating}
            onQuickRate={quickRate}
            onOpenInvoice={setInvoiceBooking}
            onCancel={setCancelling}
            onReschedule={openReschedule}
            onSos={setSosFor}
            onDispute={setNewTicket}
            /* Rebooking is booking: the service's own sheet, which is where
               adding to the basket lives. Same act, shorter list. */
            onRebook={rebook}
            onPay={payForBooking}
            /*
             * Only where there is a platform booking to refetch.
             *
             * `serviceId` is what `Booking` itself already uses to mark "this
             * came from the platform" - see its own note - rather than one of
             * the bundled examples `BookingsScreen` falls back to when
             * signed out. Refreshing one of those would spin the button and
             * change nothing, which is worse than not offering it.
             */
            onRefresh={openBooking.serviceId ? refreshBooking : undefined}
            refreshing={orders.loading}
            /*
             * Same gate as the refresh above, and for the same reason: a
             * bundled example booking has no id the platform would recognise,
             * so the button would fail every time it was pressed. Offered only
             * where there is a real booking to reissue a code for.
             */
            onResendEndOtp={openBooking.serviceId ? resendEndCode : undefined}
          />
          {sheets}
        </SafeAreaView>
      </SlideIn>
    );
  }

  /*
   * ---------------------------------------------------------------------
   * THE FOUR TABS SHARE ONE SHELL
   * ---------------------------------------------------------------------
   * Every tab renders `<SafeAreaView>` at the top, with the same three
   * children in the same order: the page, the nav, the sheets. React
   * reconciles by position, so the nav and all five modal sheets are *reused*
   * across a tab change rather than torn down and rebuilt.
   *
   * They used to differ: three tabs were wrapped in `SlideIn` and Home was
   * not, so the root element type changed on every switch to or from Home and
   * React had no choice but to unmount the entire tree - five `Modal`s
   * included. That is what the lag was, and what made the page jump.
   *
   * `SlideIn` stays on the cart, search, a subcategory and a booking: those
   * are pushes you come back from. Tabs sit beside each other and cross-fade.
   */
  if (tab === 'services') {
    return (
      <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
        <TabFade tabKey={tab}>
          <ServicesScreen
            /* The catalogue IS this page, and ops edits it while people are
               looking at it. Same read the pull on Home does. */
            onRefresh={refreshPlatform}
            categories={catalogue.categories}
            categoryFor={categoryOfService}
            servicesIn={catalogue.servicesIn}
            cart={cart}
            onChangeQuantity={changeQuantity}
            onOpenService={openServiceSheet}
            /*
              The same read Home's own banner uses, passed down rather than
              re-derived: `useCoverage` is already running on this screen for
              `outOfCoverage`, and a second copy inside `ServicesScreen` would
              mean a second geocode of the same pin on every tab switch.

              `outOfCoverage` collapses the three states to a boolean, so the
              raw hook value goes down instead - the tab needs to tell "not
              covered" from "not known yet".
            */
            serviceable={servicesCoverage}
          />

          {/*
            The same running total Home and the subcategory page carry.

            This tab is where most of the adding actually happens - it is the
            whole catalogue - and it was the one screen where adding a service
            produced no visible total and no way through to the basket. The
            only cue was the tab bar's own badge.

            Inside `TabFade` and above `BottomNav`, exactly as on Home: the bar
            sits in the column and lifts the last row of the list rather than
            covering it, and takes no bottom inset because the tab bar below it
            already clears the nav buttons.
          */}
          {/*
            Hidden while this tab is showing the coming-soon message instead
            of the catalogue. The screen above says we cannot come to this
            address; a "proceed to cart" bar pinned under it says the opposite,
            and pointing someone at a checkout that will stop them is the
            journey this message exists to prevent.

            The basket itself is untouched - the Cart tab still opens and still
            holds whatever is in it. This only removes the shortcut from a
            screen that is currently saying no.
          */}
          {servicesBlocked ? null : (
            <CartBar
              count={cartBarCount}
              total={cartTotal}
              onDismiss={hideCartBar}
              /* A tab rather than an overlay, so `setTab` alone would do - but
                 every cart bar routes through one function so none of them can
                 drift back to the broken form. */
              onPress={goToCart}
            />
          )}
        </TabFade>
        <BottomNav
          active={tab}
          onSelect={selectTab}
          cartCount={cartCount}
          /* No Services tab for an address we cannot serve - see `hideServices`. */
          hideServices={outOfCoverage}
          /* Desktop web only: the hamburger menu carries the wallet and
             notifications the header gives up there. No-ops in the bar. */
          onPressWallet={openWallet}
          onPressNotifications={() => setNotificationsOpen(true)}
          notificationCount={unreadCount}
        />
        {sheets}
      </SafeAreaView>
    );
  }

  if (tab === 'bookings') {
    return (
      <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
        <TabFade tabKey={tab} skeleton="bookings">
          <BookingsScreen
            /* Always the real list, never `undefined`. `useBookings` clears
               itself to an empty array when signed out, and an empty list is
               the true answer for a guest - passing nothing instead made the
               screen fall back to the bundled examples and show a stranger's
               booking, professional and address as theirs. */
            bookings={orders.bookings}
            loading={orders.loading}
            error={orders.error}
            onRefresh={() => {
              orders.refresh();
            }}
            services={catalogue.services}
            categoryFor={categoryFor}
            /* "Explore"/"Browse services" on the empty state - the catalogue
               now has its own tab, so this goes there rather than to Home,
               which does not itself show the full catalogue any more. */
            onBrowse={() => setTab('services')}
            /* The driver card at the top of this list is a customer ride's
               only surface in the app, so it is also the only way in to the
               alarm for somebody sitting in a car. */
            onRideSos={setSosForRide}
            onOpenBooking={setOpenBooking}
            // The whole trade, not one service - the same sheet a category tile
            // opens on Home, so a tile does the same thing wherever it is.
            onSelectCategory={openCategory}
            // The service's own sheet, which is where adding to the cart lives -
            // rebooking is the same act as booking, from a shorter list.
            /* The rail hands back a service; `rebook` wants a booking, so it is
             wrapped in the smallest one that identifies what to add. */
            onRebook={service =>
              rebook({ serviceId: service.key, title: service.name } as Booking)
            }
            /* The same slot picker the detail screen opens, driven by the same
             state - one reschedule flow with two doors into it. */
            onReschedule={openReschedule}
          />
        </TabFade>
        <BottomNav
          active={tab}
          onSelect={selectTab}
          cartCount={cartCount}
          /* No Services tab for an address we cannot serve - see `hideServices`. */
          hideServices={outOfCoverage}
          /* Desktop web only: the hamburger menu carries the wallet and
             notifications the header gives up there. No-ops in the bar. */
          onPressWallet={openWallet}
          onPressNotifications={() => setNotificationsOpen(true)}
          notificationCount={unreadCount}
        />
        {sheets}
      </SafeAreaView>
    );
  }

  if (tab === 'chat') {
    return (
      <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
        <TabFade tabKey={tab} skeleton="chat">
          <ChatScreen
            categoryFor={categoryFor}
            bookings={orders.bookings}
            onBack={chatFromSupport ? backFromChat : undefined}
            onPressNotifications={() => setNotificationsOpen(true)}
            notificationCount={unreadCount}
          />
        </TabFade>
        <BottomNav
          active={tab}
          onSelect={selectTab}
          cartCount={cartCount}
          /* No Services tab for an address we cannot serve - see `hideServices`. */
          hideServices={outOfCoverage}
          /* Desktop web only: the hamburger menu carries the wallet and
             notifications the header gives up there. No-ops in the bar. */
          onPressWallet={openWallet}
          onPressNotifications={() => setNotificationsOpen(true)}
          notificationCount={unreadCount}
        />
        {sheets}
      </SafeAreaView>
    );
  }

  /*
   * Its own tab, not a sheet raised over Home - reviewing a basket is a
   * destination, the same reasoning `CartScreen`'s own note already gave
   * this before it lived behind a boolean. Address-add still layers over it
   * as its own overlay, same as every other tab.
   */
  if (tab === 'cart') {
    return (
      <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
        {/* No matching `TabSkeletonKind` for Cart yet - omitted rather than
            borrowing Bookings' shape and showing the wrong cover. */}
        <TabFade tabKey={tab}>
          <CartScreen
            /* The basket is priced off the catalogue, so a stale price here is
               the one that ends up being charged. */
            onRefresh={refreshPlatform}
            services={catalogue.services}
            categoryFor={categoryOfService}
            cart={cart}
            onChangeQuantity={changeQuantity}
            /* Back to wherever the basket was opened from - see `cartOrigin`.
               Hardcoding Home here sent someone browsing Services back to a
               tab they were never on. */
            onBack={() => setTab(cartOrigin)}
            /* An empty basket sends you to the services tab, not back Home -
               the button offers services, so it should land on them. */
            onBrowse={() => setTab('services')}
            cartHours={cartHours}
            onChangeHours={changeHours}
            onClearCart={() => {
              setCart({});
              setCartHours({});
            }}
            onCheckout={onCheckout}
            openingPayment={openingPayment}
            address={address}
            addresses={addressBook.addresses}
            onChangeAddress={onChangeAddress}
            /*
              Both open the same screen; they differ only in where it starts.
              Add lands on the map, "Use current location" gets there via a GPS
              fix - see `addressStage`.

              Both go through `gated`, because an address is saved against an
              account. Signed out, the whole flow used to run - permission,
              map, pin, the form - and fail at the last step with the server's
              own "Missing bearer token" in a dialog. Every one of those steps
              was work the customer would have to do again.

              `gated` opens sign-in FIRST and replays the action afterwards, so
              they arrive at the map already signed in and the save at the end
              simply works.
            */
            onAddAddress={() =>
              gated(() => {
                setAddressStage('fetching');
                setAddingAddress(true);
              })
            }
            onUseCurrentLocation={() =>
              gated(() => {
                setAddressStage('fetching');
                setAddingAddress(true);
              })
            }
            /* `onOpenSub` was passed here and the cart no longer takes it: the
               "Add more items" link it fed looked up a bundled subcategory a
               live service never matches, so the button did nothing at all. It
               calls `onBrowse` now - see the note on it in `CartScreen`. */
            onOpenService={openServiceSheet}
          />
        </TabFade>
        <BottomNav
          active={tab}
          onSelect={selectTab}
          cartCount={cartCount}
          /* No Services tab for an address we cannot serve - see `hideServices`. */
          hideServices={outOfCoverage}
          /* Desktop web only: the hamburger menu carries the wallet and
             notifications the header gives up there. No-ops in the bar. */
          onPressWallet={openWallet}
          onPressNotifications={() => setNotificationsOpen(true)}
          notificationCount={unreadCount}
        />
        {sheets}
      </SafeAreaView>
    );
  }

  if (tab === 'profile') {
    /*
     * The account's own pages, over the account rather than beside it.
     *
     * Each is a full screen with its own header, so the tab bar is the only
     * thing they share with the list underneath - which is why they render in
     * place of `AccountScreen` and not as sheets over it.
     */
    const page =
      accountPage === 'edit-profile' ? (
        /*
         * Personal information - a page, not the old `EditProfileSheet`.
         *
         * The sheet showed the account through its scrim, which read as being
         * "taken back to profile" with a modal floating over it. As a page it
         * slides in like every other account screen, and Back lands on the
         * account list it was opened from.
         */
        <PersonalInfoScreen
          name={profileName}
          email={profileEmail}
          phone={phone}
          photo={profilePhoto}
          householdType={profileHousehold}
          gender={profileGender}
          onPickPhoto={pickPhoto}
          onBack={() => {
            /* Backing out abandons the checkout that sent them here. The
               basket is untouched, so Proceed to Pay starts it again - but a
               parked order left behind would resurface at whatever unrelated
               moment this page next closed. */
            pendingOrder.current = null;
            setAccountPage(null);
          }}
          onSave={next => {
            setProfileName(next.name);
            setProfileEmail(next.email || null);
            setProfileHousehold(next.householdType);
            setProfileGender(next.gender);
            setAccountPage(null);

            /*
             * Back to the checkout this interrupted, if it interrupted one.
             *
             * Read and cleared together so a later visit to this page - from
             * the account list, with no order waiting - cannot reopen a stale
             * payment screen. `next.name` rather than `profileName`: the state
             * set above is not readable until the next render, and the order
             * must not be resumed if the name somehow saved empty.
             */
            const resume = pendingOrder.current;
            pendingOrder.current = null;
            if (resume && next.name.trim()) {
              payFromCartRef.current(resume);
            }
          }}
        />
      ) : accountPage === 'settings' ? (
        <SettingsScreen
          onBack={() => setAccountPage(null)}
          // Terms and the store listing do not exist yet; the gate is the
          // honest response to both.
          /* Replaces rather than stacks. These are siblings under the account,
             not children of Settings, so back from either lands on the account
             list - which is where the rows that opened them live. */
          onOpenPrivacy={() => setAccountPage('privacy')}
          onOpenNotifications={() => setAccountPage('notifications')}
        />
      ) : accountPage === 'addresses' ? (
        <SavedAddressesScreen
          addresses={addressBook.addresses}
          current={address}
          onBack={() => setAccountPage(null)}
          onAdd={() => {
            setAccountPage(null);
            setAddingAddress(true);
          }}
          /*
           * Editing re-picks the point, which is the location flow - there is
           * no separate form that could save anywhere else.
           *
           * The address itself is carried through now. This used to drop its
           * argument and open the blank add-flow, which is why editing showed
           * an empty panel with no address in it and, on saving, added a
           * second row instead of changing the one being edited.
           */
          onEdit={target => {
            setAccountPage(null);
            setEditingAddress(target);
            setAddingAddress(true);
          }}
          /*
           * Rejects on failure so the screen can report it. If the deleted one
           * was the address in use, the header would otherwise keep showing a
           * line that no longer exists - so it is cleared back to the prompt.
           */
          onDelete={async target => {
            await addressBook.remove(target.place.id);
            if (formatAddress(target) === address) {
              /* No line means no pin. Leaving the old one behind would have
                 coverage answering about an address that is gone. */
              onChangeAddress?.(NO_ADDRESS, undefined);
            }
          }}
          /*
           * Look the pin up again and write back what comes out.
           *
           * For rows saved as "Pinned location, <lat>, <lng>" - the geocode
           * failed at save time and the app stored the failure. The pin is
           * still good, so the same lookup that failed once usually succeeds
           * now, and `placeFromPin` turns it into the same line a fresh save
           * would have produced.
           *
           * Throws on a pin that still resolves to nothing, so the row can say
           * so rather than silently redrawing itself unchanged.
           */
          onRepair={async target => {
            if (!target.coords) {
              throw new Error(
                'This address has no pin saved, so there is nothing to look up. Use Edit to place it on the map.',
              );
            }

            const resolved = await reverseGeocode(
              target.coords.lat,
              target.coords.lng,
            );
            const place = placeFromPin(
              resolved,
              { latitude: target.coords.lat, longitude: target.coords.lng },
              target.place.id,
            );

            if (place.title === UNRESOLVED_TITLE) {
              throw new Error(
                'We still could not read an address for that pin. Use Edit to move it slightly.',
              );
            }

            await addressBook.update({
              id: target.place.id,
              label: target.label,
              addressLine: `${place.title}, ${place.subtitle}`,
              landmark: target.note,
              lat: target.coords.lat,
              lng: target.coords.lng,
            });
          }}
          onSetDefault={target => addressBook.setDefault(target.place.id)}
        />
      ) : accountPage === 'payment' ? (
        <PaymentMethodsScreen
          onBack={() => setAccountPage(null)}
          // Nothing to add a card to yet; the gate is the honest response.
          onUnavailable={() => gated(() => {})}
        />
      ) : accountPage === 'ratings' ? (
        <RatingsScreen
          onBack={() => setAccountPage(null)}
          reviews={reviews}
          /* Editing is the composer again, on the booking the review is
             about - one review per booking, so there is nothing else it
             could open. */
          onEdit={bookingId => {
            const booking = allBookings.find(item => item.id === bookingId);
            if (booking) {
              /* The ratings page deliberately stays open underneath - the
                 composer is an overlay now, so closing it lands back on this
                 list rather than on the account. Clearing `accountPage` here
                 is what used to put the profile in the background. */
              setRating(booking);
            }
          }}
          onHidePhoto={(bookingId, photoId) =>
            setReviews(current => {
              const review = current[bookingId];
              if (!review) {
                return current;
              }
              return {
                ...current,
                [bookingId]: {
                  ...review,
                  photos: review.photos.map(photo =>
                    photo.id === photoId ? { ...photo, hidden: true } : photo,
                  ),
                },
              };
            })
          }
        />
      ) : accountPage === 'privacy' ? (
        <PrivacyScreen
          onBack={() => setAccountPage(null)}
          /* The page's one control - guideline 5.1.1(v) requires deletion to
             work from inside the app. Gated because there is nothing to
             delete until somebody is signed in. The dead rows the page used
             to carry (phone change, devices, export, the four switches) are
             gone with their `onUnavailable` gate; each returns from git
             history when its backend exists. */
          onDeleteAccount={() => gated(() => setConfirmingDelete(true))}
        />
      ) : accountPage === 'support' ? (
        <HelpSupportScreen
          onBack={() => setAccountPage(null)}
          openTickets={tickets.length}
          onOpenTickets={() => setTicketsOpen(true)}
          onNewTicket={() => setNewTicket(true)}
          onStartChat={() => {
            setAccountPage(null);
            setChatFromSupport(true);
            setTab('chat');
          }}
        />
      ) : accountPage === 'notifications' ? (
        <NotificationsScreen
          notifications={notifications}
          onBack={() => setAccountPage(null)}
          onRead={markRead}
          onReadAll={markAllRead}
        />
      ) : accountPage === 'refer' ? (
        /*
         * Ungated, unlike most things that need an account.
         *
         * The offer is the reason to sign in, so hiding it behind a sign-in
         * inverts the funnel - somebody has to already be a customer to find
         * out why they would want to be one. The screen renders the terms
         * either way and asks for the account only where it genuinely needs
         * one: to key a code that can actually be paid out.
         */
        <ReferralScreen
          onBack={() => setAccountPage(null)}
          phone={phone}
          signedIn={signedIn}
          onSignIn={() => gated(() => {})}
        />
      ) : accountPage === 'subscription' && membershipOnSale() ? (
        <SubscriptionScreen
          onBack={() => setAccountPage(null)}
          onSignIn={() => gated(() => {})}
        />
      ) : null;

    /*
     * The account list stays MOUNTED underneath whichever sub-page is open.
     *
     * The sub-page used to render in place of `AccountScreen`, which unmounted
     * the list - so its `ScrollView` forgot where it was, and backing out of
     * any page landed at the top of the account again. As an overlay, the list
     * and its scroll position survive untouched; the page slides in over it
     * and Back simply lifts it away.
     */
    return (
      <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
        <TabFade tabKey="account" skeleton="profile">
          <AccountScreen
            signedIn={signedIn}
            name={profileName}
            phone={phone}
            email={profileEmail}
            photo={profilePhoto}
            stats={accountStats}
            onSignIn={() => setAuthOpen(true)}
            onSelect={action => {
              /* Confirmed first, not done on the bare tap - see the sheet in
                 `sheets` for what it says and where confirming lands. */
              if (action === 'logout') {
                setConfirmingLogout(true);
                return;
              }
              // Editing needs an account to edit, so it goes through the gate
              // like everything else rather than opening an empty form.
              if (action === 'edit-profile') {
                gated(() => setAccountPage('edit-profile'));
                return;
              }
              // My Bookings is the Bookings tab, not a page of its own - the
              // list already exists there and two copies would drift. Back
              // returns here, same as the stat strip - see `bookingsOrigin`.
              if (action === 'bookings' || action === 'history') {
                setBookingsOrigin('profile');
                setTab('bookings');
                return;
              }
              /*
               * The account's own pages.
               *
               * Ungated on purpose. Each is either seed data or a phone number,
               * and asking someone to sign in before they can read the help
               * desk is the wrong order - support is what you reach for when
               * signing in is the thing going wrong.
               */
              /*
               * The wallet is not an account sub-page.
               *
               * It already exists as its own full-screen route - a slide-in
               * with a bleeding banner and its own back handling, opened from
               * the Home header too. Routing this row through `setAccountPage`
               * would have meant a second copy of the same screen that could
               * drift from the first, so the row opens the one that is there.
               */
              if (action === 'wallet') {
                openWallet();
                return;
              }
              if (
                action === 'settings' ||
                action === 'addresses' ||
                action === 'payment' ||
                action === 'ratings' ||
                action === 'support' ||
                action === 'privacy' ||
                action === 'notifications' ||
                action === 'refer' ||
                /* Only while on sale - the row is hidden otherwise, and this
                   keeps any other way of raising the action from opening a
                   page the server would refuse to sell from. */
                (action === 'subscription' && membershipOnSale())
              ) {
                setAccountPage(action);
                return;
              }
              // The rest have no destination yet. Signing in is the prerequisite
              // for all of them either way, so route through the same gate: it
              // asks for an account when there is none and does nothing when
              // there is, rather than a dead tap in both cases.
              gated(() => {});
            }}
          />
        </TabFade>

        {/*
          The open sub-page, over the list rather than instead of it - see the
          note above the return. It slides in like the pushed screens do,
          because that is what it now is: a push you come back from.
        */}
        {page ? (
          <View style={styles.addingAddress}>
            <SlideIn>{page}</SlideIn>
          </View>
        ) : null}

        <BottomNav
          active={tab}
          onSelect={selectTab}
          cartCount={cartCount}
          /* No Services tab for an address we cannot serve - see `hideServices`. */
          hideServices={outOfCoverage}
          /* Desktop web only: the hamburger menu carries the wallet and
             notifications the header gives up there. No-ops in the bar. */
          onPressWallet={openWallet}
          onPressNotifications={() => setNotificationsOpen(true)}
          notificationCount={unreadCount}
        />

        {sheets}
      </SafeAreaView>
    );
  }

  return (
    /*
     * No `top` edge, and only here.
     *
     * Home opens on `HomeHero`, whose artwork is meant to run under the status
     * bar - its own style sheet says so, and only its bottom corners are
     * rounded. Claiming the top inset here painted it in `colors.background`
     * instead, which put a flat pale band above a rounded picture. The hero
     * takes the inset itself and pads its controls clear of it, so the bleed
     * costs no legibility.
     *
     * The other tabs keep the edge: they open on text and controls with no
     * artwork behind them, and those must not slide under the clock.
     */
    <SafeAreaView style={styles.root} edges={['left', 'right']}>
      <TabFade tabKey={tab} skeleton="home">
        <ScrollView
          style={styles.scroll}
          /*
          `column` is the whole tablet story for this page: zero on every
          phone, and on anything wider than 560pt it narrows the page to a
          readable column and centres it. Everything inside keeps measuring
          itself against its parent, so the hero, the grid and the rails all
          follow without knowing anything about it.
        */
          contentContainerStyle={[styles.content, resp.column]}
          showsVerticalScrollIndicator={false}
          // Detaches off-screen rows from the native view tree on Android, which
          // is what keeps a long catalogue scrolling smoothly.
          removeClippedSubviews
          /*
          The same gesture as every other screen with server data behind it -
          the pull-down was already possible here (the page bounces), it just
          answered with nothing, which read as broken.

          It re-reads the bookings AND the catalogue. It used to be the
          bookings alone, on the reasoning that the catalogue "refreshes itself
          on launch" - which is true and is not enough: the thing most likely
          to be stale on this screen is a price, ops changes those while people
          have the app open, and the only way to see one was to kill the app
          and start again. A customer who pulled watched the spinner turn and
          every card stay exactly as it was.

          `catalogue.reload()` deliberately bypasses the foreground throttle;
          see the note on it. Both are awaited - see `pullToRefresh` - so the
          spinner stops when the slower of the two has landed rather than when
          the bookings have.
        */
          refreshControl={
            <RefreshControl
              refreshing={pulling || orders.loading}
              onRefresh={pullToRefresh}
              tintColor={colors.primary}
              colors={[colors.primary]}
            />
          }
        >
          {/* ----------------------------------------------------------- hero */}
          {/*
            Backgrounds bleed, content does not - the split the wide web page
            needs. The scroller's `column` above centres EVERYTHING, hero
            included, which on a monitor leaves the page background showing
            either side of the hero's artwork from the very top of the screen.
            This wrapper pulls the hero back out to the window’s true edges (the
            same negative-margin bleed `ServicesScreen` uses for its banners);
            the hero then re-constrains its own overlay content - header, search
            field, banner copy - to the centred column internally, so only the
            picture gets wider. Web only: `sideInset` is 0 on every phone
            anyway, and a native tablet keeps the composition it has today.
          */}
          <View
            style={
              Platform.OS === 'web'
                ? { marginHorizontal: -resp.sideInset }
                : null
            }
          >
            <HomeHero
              address={address}
              /* The catalogue's own names - see `hintsFrom`. A hint that finds
                 nothing reads as search being broken. */
              hints={hints}
              notificationCount={unreadCount}
              onPressAddress={() => setAddressOpen(true)}
              onPressNotifications={() => setNotificationsOpen(true)}
              onPressWallet={openWallet}
              // No search screen yet, so this opens the full catalogue.
              // A field that says "Search for carpentry" should let you type. It
              // used to open the catalogue sheet, which answers a different
              // question - here is a list of categories to browse.
              onPressSearch={() => setSearchOpen(true)}
              /*
               * A banner with no category is the general offer, and `HeroBanner`
               * documents that as opening the whole catalogue.
               *
               * A banner WITH one is not automatically safe, though: the banner
               * copy is written ahead of the catalogue it links to, and the
               * catalogue now comes from the server - a category the banner
               * still mentions can be renamed or retired there without this
               * screen knowing. `categoryFor` is the wrong tool for deciding
               * that: it exists to give something ROOM-SAFE to render an icon
               * and a label from, so a miss quietly resolves to the first
               * category rather than throwing - which is fine for a colour and
               * a word, and wrong for navigation, where it means the tap opened
               * a trade nobody advertised. This looks the key up for real
               * instead, and falls back to the whole catalogue exactly where a
               * banner with no category already does - "the thing this was
               * promoting isn't there, here is everything that is."
               */
              onPressBanner={category => {
                const match = category
                  ? catalogue.categories.find(item => item.key === category)
                  : undefined;
                if (match) {
                  setPickingCategory(match);
                } else {
                  setTab('services');
                }
              }}
              /*
               * Published banners, lifted out of the tree. Empty or absent means
               * the compiled ones are drawn - see the prop's own note.
               */
              banners={publishedBanners}
              onPressPublishedBanner={action => {
                /*
                 * The three destinations the server's validator accepts, with
                 * the same fallback the compiled banners already use: anything
                 * that cannot be resolved opens the catalogue rather than doing
                 * nothing. A tap that appears to fail is worse than one that
                 * lands somewhere reasonable.
                 *
                 * The validator checked these targets EXISTED when the tree was
                 * published. It cannot promise this device has loaded them, or
                 * that they are still live in this city - so every branch checks
                 * before it navigates, for the reason the note above gives.
                 */
                if (action?.type === 'category') {
                  const match = catalogue.categories.find(
                    item => item.key === action.target,
                  );
                  if (match) {
                    setPickingCategory(match);
                    return;
                  }
                }

                if (action?.type === 'service') {
                  const match = catalogue.services.find(
                    item => item.key === action.target,
                  );
                  if (match) {
                    openServiceSheet(match);
                    return;
                  }
                }

                if (action?.type === 'url') {
                  Linking.openURL(action.target).catch(() => undefined);
                  return;
                }

                setTab('services');
              }}
            />
          </View>

          {/*
          Not in this city yet - the message INSTEAD of everything below it.
          ---------------------------------------------------------------------
          The hero above stays, and that is the whole design: it carries the
          address switcher, which is the one control that can turn this answer
          around. Everything under it - categories, promos, rails, guarantees -
          is an invitation to book, and there is nothing here to book. Offering
          the catalogue anyway is what let someone fill a basket and meet the
          wall at checkout, which is the journey this message exists to end.

          Only an explicit `false`. `null` (not known yet) and `true` both fall
          through to the normal page, so a slow `/cities` never blanks Home for
          a customer we can actually serve.
        */}
          {outOfCoverage ? (
            <View style={styles.blocked}>
              <EmptyState
                icon="pin"
                title="We're coming to your city soon"
                message={
                  "We don't serve this area yet. We're expanding fast - we'll let you know the moment we arrive."
                }
                actionLabel="Change address"
                onAction={() => setAddressOpen(true)}
              />
            </View>
          ) : catalogueFailed ? (
            /*
            The catalogue could not be reached - said out loud, in the same
            place and the same shape as the out-of-coverage message above.
            ---------------------------------------------------------------
            This branch is new because the answer to a failed request used to
            be the bundled seed: a screen full of cards for services the
            platform stopped selling, every one of which failed at checkout on
            an id the server has never heard of. A customer met the wall four
            screens in, having filled a basket.

            Nothing is lost by showing this instead. The API is unreachable, so
            no booking could have been made from those cards anyway - all that
            changes is WHERE the customer is told, and this is the honest end
            of the funnel rather than the expensive one.

            Deliberately not the coverage message: "we don't serve your area"
            and "we couldn't reach the server" are different facts, and only
            one of them has a Retry.
          */
            <View style={styles.blocked}>
              <ErrorState
                title="Couldn't load services"
                message="Check your connection and try again."
                onRetry={catalogue.reload}
              />
            </View>
          ) : (
            <>
              {/*
                The city is live but this exact spot is not - the soft signal.
                Only when the server says so (area gate on, pin outside every
                served area); the catalogue below stays, greyed where it
                cannot be booked.
              */}
              {areaServices.outsideAreas ? (
                <AreaNotServedNotice
                  onChangeAddress={() => setAddressOpen(true)}
                />
              ) : null}
              {/* ----------------------------------------------------- categories */}
              {/*
                Ops' categories when the tree names any, this screen's own when
                it does not - see `publishedCategories`.

                The published branch has no "View all": that control opens the
                whole catalogue, and a tree that has deliberately chosen which
                categories to show is not asking for a door to the ones it left
                out. The compiled branch keeps it, because it is showing the
                first six of everything.
              */}
              {publishedCategories ? (
                <ConfigRenderer
                  tree={publishedCategories}
                  services={catalogue.services}
                  categories={catalogue.categories}
                  serviceCardWidth={serviceTileWidth}
                  onPressService={openServiceSheet}
                  /* The renderer has already dropped any slug this device does
                     not have, so every key that reaches here resolves - and
                     the tap does exactly what the compiled grid's does. */
                  onPressCategory={openCategory}
                  renderBanners={() => null}
                />
              ) : (
                <View style={styles.section}>
                  <SectionHeader
                    title="Categories"
                    actionLabel="View all"
                    /* The rest of THIS rail, as a sheet over Home - not the Services
               tab, which answers a question about categories by replacing the
               page with a flat list of every service. */
                    onPressAction={() => setCatalogueOpen(true)}
                  />
                  {/*
                  The swiped rail, which is what this section was before it
                  spent a day as a two-across grid.

                  The grid's argument was that everything past the third tile
                  hid behind a sideways gesture. True - and the client wants
                  the rail back, because the wrapped version pushed Popular
                  Services a screen and a half down and made the page open on
                  categories instead of on something to buy. The rail is one
                  line whatever the count, and its own scroll carries the page
                  margin, the same as the rails below it.

                  Tapping one raises the subcategory sheet rather than
                  filtering in place - the rail is a way in, not a filter.
                */}
                  <CategoryRail
                    categories={homeCategories}
                    onSelect={openCategory}
                  />
                </View>
              )}

              {/* --------------------------------------------------------- driver */}
              {/*
          Hidden, not deleted.

          The ride card was the only entrance to the driving service from Home.
          It is commented out rather than removed because the whole feature
          behind it is still wired and reachable - `driverOpen`, `DriverScreen`
          and the live-ride polling in `DriverCard` are all still here - and
          deleting the render would leave that code with no way in and no
          record of why. Restore this block to bring the service back.

          <View style={styles.driver}>
            <DriverCard onPress={() => setDriverOpen(true)} />
          </View>
        */}

              {/*
                ------------------------------------------------- refer & earn

                The offer was reachable only from Profile → Refer & Earn, four
                taps in, on a page somebody opens to change their name or read
                a policy. An offer nobody finds is an offer nobody takes.

                Between the categories and Popular Services, at the client's
                asking. It sat under the hero, which is the first thing the eye
                lands on - and that is exactly why it moved: the page opened on
                an offer rather than on something to buy, and a customer who
                came to book a service met an invitation first.

                Here it reads as a break between two blocks of catalogue, seen
                by anybody who scrolls past the categories, and Popular
                Services still starts within the first screen and a half.

                One wide picture and nothing else. The artwork already carries
                the words, so a heading and a body beside it would say
                everything twice; and it is the SAME file the referral screen
                opens with, so tapping it lands on a page that looks like what
                was tapped rather than on a stranger.

                Ungated, exactly as the destination is - see the note on the
                'refer' branch. The offer is the reason to sign in, so putting
                sign-in in front of it inverts the funnel.
              */}
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Refer and earn. Invite friends and you both get rewarded."
                onPress={openReferral}
                style={({ pressed }) => [
                  styles.referBanner,
                  /* A minimum, so a phone with large text grows the card
                     rather than clipping the copy inside it. */
                  { minHeight: referHeight },
                  pressed ? styles.pressed : null,
                ]}
              >
                {/*
                  Sized explicitly rather than with `StyleSheet.absoluteFill`.

                  An `Image` with a source that has intrinsic dimensions does
                  not reliably shrink to left/right/top/bottom insets on
                  Android: it drew at the artwork's own 2243pt, anchored left,
                  and `overflow: hidden` clipped the rest - so the card showed
                  the picture's empty left half and the gift was outside the
                  frame entirely. A width and a height leave nothing to infer.
                */}
                <Image
                  source={REFER_ART}
                  style={styles.referArt}
                  resizeMode="cover"
                />

                {/* Held to the left half. The artwork's own gift and coins own
                    the right, and copy running under them is copy nobody can
                    read. */}
                <View style={styles.referText}>
                  <Text variant="title" color={colors.ink}>
                    Refer & Earn
                  </Text>
                  <Text variant="caption" color={colors.inkBody}>
                    Invite a friend - you both get rewarded.
                  </Text>

                  {/*
                    Filled, not outlined, and not its own `Pressable`.

                    A banner with no control on it is read as decoration and
                    scrolled past - which is how this offer stayed buried in
                    the profile. And the whole card goes to one place, so a
                    button inside it would be two targets for one destination.
                  */}
                  <View style={styles.referCta}>
                    <Text variant="labelSm" color={colors.onPrimary}>
                      INVITE NOW
                    </Text>
                  </View>
                </View>
              </Pressable>

              {/* ------------------------------------------------ popular services */}
              {/*
                Ops' blocks when there are any, and the compiled rail when
                there are not.

                `popularFrom` picks its services by matching NAMES against a
                seed list inside the app - so renaming a service in the admin
                console silently drops it out of "Popular", and nothing here
                could ever have said which services belong on the home screen.
                A published `service_carousel` names them by id instead.

                The compiled rail stays as the fallback rather than being
                deleted: it is what every deployment without a publication
                still draws, and it is the screen this app shipped with.
              */}
              {publishedServices ? (
                <ConfigRenderer
                  tree={publishedServices}
                  services={catalogue.services}
                  categories={catalogue.categories}
                  /* The GRID width, not the rail width. `ConfigRenderer` draws a
                     wrapping grid now, so a card sized for a sideways rail -
                     about two thirds of the page - fits one to a row and
                     leaves the rest of the row empty. */
                  serviceCardWidth={serviceTileWidth}
                  onPressService={openServiceSheet}
                  onPressCategory={key => {
                    const match = catalogue.categories.find(
                      item => item.key === key,
                    );
                    if (!match) return;
                    /* The same answer `openCategory` gives, for this one
                       path that opens the picker directly. */
                    if (getAreaAvailability().categories.has(key)) {
                      setComingSoonFor(match.label);
                      return;
                    }
                    setPickingCategory(match);
                  }}
                  /* Banners were lifted out for the hero - see
                     `publishedBanners`. Nothing left in the tree renders one. */
                  renderBanners={() => null}
                />
              ) : (
                <View style={styles.section}>
                  <SectionHeader
                    title="Popular Services"
                    actionLabel="View all"
                    onPressAction={() => setTab('services')}
                  />
                  {/*
                    A grid, not a rail.

                    -----------------------------------------------------------
                    WHY TWO COLUMNS AND NOT THREE
                    -----------------------------------------------------------
                    The category tiles above run three across and that is right
                    for them: a tile carries a picture and one short word. A
                    service card carries three lines - a name that runs to two,
                    a struck price beside a real one, and a duration. At three
                    across each card is about 110pt on a normal phone, which
                    turns "Post-House Shifting Cleaning – Old House" into four
                    clipped lines and puts "₹199 ₹99" on two.

                    Two across is the width the card was drawn for, so nothing
                    about the card itself changes - only where it sits.

                    -----------------------------------------------------------
                    WHY A GRID AT ALL
                    -----------------------------------------------------------
                    A sideways rail shows three cards and hides the rest behind
                    a gesture nothing on screen asks for. A grid scrolls with
                    the page somebody is already scrolling, so the whole
                    catalogue is reachable without learning a second one - see
                    `popular`, which no longer truncates.
                  */}
                  <View style={styles.serviceGrid}>
                    {popular.map(service => (
                      <ServiceCard
                        key={service.key}
                        service={service}
                        category={categoryFor(service.category)}
                        width={serviceTileWidth}
                        onPress={openServiceSheet}
                      />
                    ))}
                  </View>
                </View>
              )}

              {/*
          No per-category rails here.

          Eight of them repeated the whole catalogue below the fold, four
          services at a time, when the grid above already offers every category
          in two rows. Browsing by trade goes through the grid and its
          subcategory sheet; this was a second, longer route to the same
          services.
        */}

              {/*
                Copy and spacing from the tree, if it carries any.

                These have nothing compiled to stand beside - this screen owns
                a banner, a category block and a service block, and a
                `text_block` is whatever ops wanted to say that none of those
                could. So they go last, in the tree's own order, above the
                trust strip that closes the page.

                Without this they were silently dropped the moment the tree
                stopped being rendered as one piece.
              */}
              {publishedExtras ? (
                <ConfigRenderer
                  tree={publishedExtras}
                  services={catalogue.services}
                  categories={catalogue.categories}
                  serviceCardWidth={serviceTileWidth}
                  onPressService={openServiceSheet}
                  onPressCategory={openCategory}
                  renderBanners={() => null}
                />
              ) : null}

              {/* ----------------------------------------------------- guarantees */}
              <View style={styles.guarantees}>
                {guarantees.map(item => (
                  <View key={item.label} style={styles.guarantee}>
                    <ArtPanel icon={item.icon} style={styles.guaranteeArt} />
                    <Text variant="caption" align="center">
                      {item.label}
                    </Text>
                  </View>
                ))}
              </View>
            </>
          )}

          <View style={styles.footer}>
            <Icon name="leaf" size={18} color={colors.borderStrong} />
            <Text variant="caption" align="center">
              Homingo · verified help for every corner of your home
            </Text>
          </View>
        </ScrollView>

        {/* Above the tab bar and in the column, so it lifts the last row of
          content rather than sitting on top of it.

          Hidden while Home is showing the coming-soon message: the screen says
          we cannot come to this address, and a "proceed to cart" bar pinned
          under it says the opposite. The basket itself is untouched. */}
        {/* Above the cart bar, because a job under way outranks a basket that
            has not been paid for. Both sit in the column over the tab bar. */}
        <LiveBookingBar
          bookings={liveBarBookings}
          onPress={booking => {
            /*
             * The tab is deliberately left alone.
             *
             * It used to switch to Bookings first, "the same way every other
             * route into a booking does" - but every other route STARTS on
             * the bookings list, and this one does not. The customer opened
             * the booking from Home, so Back has to put them back on Home;
             * landing them on a list they never visited is the app moving
             * them somewhere they did not ask to go.
             */
            setOpenBooking(booking);
          }}
          onRate={(booking, stars) => {
            quickRate(booking, stars);
            setRatingHidden(booking.id);
          }}
          onDismiss={booking => setRatingHidden(booking.id)}
        />

        {outOfCoverage ? null : (
          <CartBar
            count={cartBarCount}
            total={cartTotal}
            onDismiss={hideCartBar}
            onPress={goToCart}
          />
        )}
      </TabFade>

      <BottomNav
        active={tab}
        onSelect={selectTab}
        cartCount={cartCount}
        /* No Services tab for an address we cannot serve - see `hideServices`. */
        hideServices={outOfCoverage}
      />

      {/*
        Both sheets sit outside the ScrollView. They render into a Modal, so
        their position in the tree does not affect where they appear - but
        keeping them here makes it obvious they belong to the screen rather
        than to any one card.
      */}
      {/*
        Every category, raised over Home - what "View all" opens.

        Only a picker: choosing one hands straight to `openCategory`, the same
        route a tap on the rail takes, so there is one path into a category
        rather than two that can drift apart. Closed first, because stacked
        modals misbehave on Android.
      */}
      <CatalogueSheet
        visible={catalogueOpen}
        onClose={() => setCatalogueOpen(false)}
        categories={catalogue.categories}
        address={address}
        onSelectCategory={key => {
          setCatalogueOpen(false);
          openCategory(key);
        }}
      />

      <SubCategorySheet
        category={pickingCategory}
        onClose={() => setPickingCategory(null)}
        // Close before opening the page: stacked modals misbehave on Android.
        onSelect={sub => {
          setPickingCategory(null);
          setOpenSub(sub);
        }}
      />

      {sheets}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  /* Gives the coming-soon message the height the catalogue would have had, so
     it sits in the middle of the page rather than clinging under the hero. */
  blocked: {
    minHeight: 420,
    justifyContent: 'center',
  },
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  addingAddress: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.background,
  },
  scroll: {
    flex: 1,
  },
  content: {
    paddingBottom: spacing.xxl,
  },

  /* Overlaps the hero's rounded lower edge. */
  search: {
    marginTop: -spacing.xxl,
  },
  driver: {
    marginTop: spacing.md,
  },
  /* Pulled up over the hero's rounded lower edge. */

  /*
   * The same 12 the page is guttered by, down from 24.
   *
   * Every section already announces itself with a heading, so the space was
   * doing no work that the heading was not already doing - it just pushed the
   * catalogue down the screen. One number for the horizontal inset and the
   * vertical step also means the page has a single rhythm instead of two.
   */
  section: {
    marginTop: spacing.md,
    /*
     * Clips the horizontal rail to the section's own bounds. Native
     * ScrollViews clip their content as a matter of course; the web build's
     * does not, and on a wide monitor the Popular Services cards painted
     * straight past the centred column to the window's edge - under the
     * hamburger, past the margin everything else respects. Costs nothing on
     * native, where the clip already happened.
     */
    overflow: 'hidden',
  },
  /* Kept: `ConfigRenderer`'s `service_carousel` still draws a rail, and a
     published layout is free to ask for one. */
  serviceRail: {
    paddingHorizontal: screenPadding,
    gap: spacing.sm,
    alignItems: 'flex-start',
  },
  /*
   * Two per row, wrapping. `flex-start` so a final odd card sits at the left
   * where the column is, rather than stretching to fill the row.
   */
  /*
   * The referral banner: a full-width picture, the page's own gutters, and the
   * card radius every other block on Home uses.
   *
   * `aspectRatio` rather than a height - the artwork is 3:1 and a fixed height
   * would crop it differently on every screen width. 3:1 also keeps it clearly
   * shorter than the hero above it, so it reads as an offer under the banner
   * rather than as a second banner.
   */
  /* Faint on the press, the same feedback every other tappable card on this
     page gives. */
  pressed: {
    opacity: 0.85,
  },
  /*
   * The card, on the dark band green - the only inverted surface in this app,
   * which is what makes it the one block on a cream page that cannot be
   * scrolled past without being seen.
   *
   * A row: copy, then the gift, then the button. Height comes in as
   * `minHeight` from `referHeight`; see the note there for why it is not an
   * `aspectRatio`.
   */
  /*
   * The card. Height comes in from `referHeight`, which holds the artwork's
   * own ratio - see `REFER_ASPECT`.
   *
   * `marginTop` is the fix for the banner reading as part of the hero above
   * it: with only a bottom margin the two surfaces met with nothing between
   * them, so the promo looked like the lower half of the hero rather than a
   * card of its own. `xl` is the same air the sections below it get.
   */
  referBanner: {
    marginTop: spacing.xl,
    marginHorizontal: screenPadding,
    marginBottom: spacing.lg,
    borderRadius: radii.lg,
    overflow: 'hidden',
    justifyContent: 'center',
  },
  /*
   * The left half, and only the left half. `maxWidth` rather than a flex
   * share because the artwork's clear area is a property of the PICTURE, not
   * of the row - 58% is where the coins begin.
   */
  referArt: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  referText: {
    maxWidth: '58%',
    paddingLeft: spacing.lg,
    paddingRight: spacing.sm,
    gap: 4,
  },
  referCta: {
    alignSelf: 'flex-start',
    marginTop: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: 7,
    borderRadius: radii.pill,
    backgroundColor: colors.primary,
  },
  serviceGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    paddingHorizontal: screenPadding,
    gap: spacing.sm,
  },

  guarantees: {
    flexDirection: 'row',
    marginTop: spacing.md,
    marginHorizontal: screenPadding,
    paddingVertical: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
  },
  guarantee: {
    flex: 1,
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.xs,
  },
  guaranteeArt: {
    width: 38,
    height: 38,
    borderRadius: 19,
  },

  footer: {
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.md,
    paddingHorizontal: screenPadding,
  },
});

export default HomeScreen;
